import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  BookOpen,
  Shield,
  BookMarked,
  Activity,
  TrendingUp,
  TrendingDown,
  Newspaper,
  Target,
  AlertTriangle,
  CheckCircle,
  Info,
  DollarSign,
  Percent,
  ArrowUpRight,
  ArrowDownRight,
  BarChart3,
  Zap,
  Clock,
  Calculator,
  Wallet,
  Scale,
  GraduationCap,
  Lock,
  Sparkles
} from 'lucide-react';
import OnboardingTutorial from '../components/OnboardingTutorial';
import TutorialsTab from '../components/learn/TutorialsTab';
import { LEARN_SLIDES } from '../config/tutorialSlides';
import { DisclaimerBanner } from '../components/LegalDisclaimer';
import HowItWorks from '../components/HowItWorks';

const Learn = () => {
  const { language } = useLanguage();
  const { isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState('rules');

  // Section Card Component
  const SectionCard = ({ icon: Icon, title, children, color = 'text-primary', bgColor = 'bg-primary/10' }) => (
    <Card className="bg-card/50 border-border/50 mb-4">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <div className={`p-2 rounded-lg ${bgColor}`}>
            <Icon className={`w-5 h-5 ${color}`} />
          </div>
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );

  // Definition Item Component
  const DefinitionItem = ({ term, definition, example, type }) => (
    <div className="mb-4 p-4 bg-muted/30 rounded-lg border border-border/30">
      <div className="flex items-center gap-2 mb-2">
        <span className="font-bold text-primary">{term}</span>
        {type && (
          <Badge variant="outline" className="text-xs">
            {type}
          </Badge>
        )}
      </div>
      <p className="text-sm text-muted-foreground mb-2">{definition}</p>
      {example && (
        <div className="text-xs bg-background/50 p-2 rounded border-l-2 border-primary/50">
          <span className="font-medium text-primary">Exemple : </span>
          <span className="text-muted-foreground">{example}</span>
        </div>
      )}
    </div>
  );

  // Risk Ratio Card Component
  const RatioCard = ({ ratio, description, verdict, color }) => (
    <div className={`p-4 rounded-lg border ${color === 'red' ? 'bg-red-500/10 border-red-500/30' : color === 'yellow' ? 'bg-yellow-500/10 border-yellow-500/30' : 'bg-green-500/10 border-green-500/30'}`}>
      <div className={`text-2xl font-bold mb-2 ${color === 'red' ? 'text-red-400' : color === 'yellow' ? 'text-yellow-400' : 'text-green-400'}`}>
        {ratio}
      </div>
      <p className="text-sm text-muted-foreground mb-2">{description}</p>
      <Badge variant={color === 'red' ? 'destructive' : color === 'yellow' ? 'secondary' : 'default'}>
        {verdict}
      </Badge>
    </div>
  );

  return (
    <div className="container mx-auto px-4 py-8 max-w-5xl" data-testid="learn-page">
      {/* Onboarding Tutorial (auto-show on first visit) */}
      <OnboardingTutorial
        pageId="learn"
        slides={LEARN_SLIDES}
        storageKey="tutorial_learn_seen"
      />

      {/* Header */}
      <div className="mb-8 text-center">
        <div className="flex items-center justify-center gap-3 flex-wrap mb-2">
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <BookOpen className="w-8 h-8 text-primary" />
            Principes de Trading
          </h1>
          <HowItWorks pageKey="learn" />
        </div>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          Maîtrisez les fondamentaux du trading pour valider les analyses de l'IA et prendre des décisions éclairées
        </p>
      </div>

      {/* Navigation Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-4 lg:grid-cols-7 mb-8 h-auto p-1">
          <TabsTrigger value="tutorials" className="flex flex-col gap-1 py-2 data-[state=active]:bg-emerald-500/20" data-testid="tutorials-tab">
            <GraduationCap className="w-4 h-4" />
            <span className="text-xs">Tutoriels</span>
          </TabsTrigger>
          <TabsTrigger value="rules" className="flex flex-col gap-1 py-2 data-[state=active]:bg-primary/20">
            <Shield className="w-4 h-4" />
            <span className="text-xs">Règles</span>
          </TabsTrigger>
          <TabsTrigger value="lexicon" className="flex flex-col gap-1 py-2 data-[state=active]:bg-primary/20">
            <BookMarked className="w-4 h-4" />
            <span className="text-xs">Lexique</span>
          </TabsTrigger>
          <TabsTrigger value="indicators" className="flex flex-col gap-1 py-2 data-[state=active]:bg-primary/20">
            <Activity className="w-4 h-4" />
            <span className="text-xs">Indicateurs</span>
          </TabsTrigger>
          <TabsTrigger value="market" className="flex flex-col gap-1 py-2 data-[state=active]:bg-primary/20">
            <TrendingUp className="w-4 h-4" />
            <span className="text-xs">Marché</span>
          </TabsTrigger>
          <TabsTrigger value="news" className="flex flex-col gap-1 py-2 data-[state=active]:bg-primary/20">
            <Newspaper className="w-4 h-4" />
            <span className="text-xs">News Éco</span>
          </TabsTrigger>
          <TabsTrigger value="practice" className="flex flex-col gap-1 py-2 data-[state=active]:bg-primary/20">
            <Target className="w-4 h-4" />
            <span className="text-xs">Pratique</span>
          </TabsTrigger>
        </TabsList>

        {/* Guest gating: show teaser + CTA when not authenticated */}
        <div className={!isAuthenticated ? 'relative' : ''} data-testid="learn-gate-wrapper">
          <div
            className={!isAuthenticated ? 'max-h-[520px] overflow-hidden pointer-events-none select-none' : ''}
            aria-hidden={!isAuthenticated}
          >


        {/* ===== TUTORIELS DE L'APPLICATION ===== */}
        <TabsContent value="tutorials" className="mt-0" data-testid="tutorials-content">
          <TutorialsTab />
        </TabsContent>

        {/* ===== RÈGLES FONDAMENTALES ===== */}
        <TabsContent value="rules" className="mt-0">
          <SectionCard icon={Shield} title="Règles Fondamentales" color="text-green-400" bgColor="bg-green-500/10">
            <div className="space-y-6">
              {/* Rule 1: Risk Management */}
              <div className="p-4 bg-gradient-to-r from-red-500/10 to-transparent rounded-lg border border-red-500/20">
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                  <h3 className="font-bold text-red-400">Règle d'Or : Gestion du Risque</h3>
                </div>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    <span><strong>Ne risque jamais plus de 1 à 2%</strong> de ton capital total sur un seul trade.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    <span><strong>Utilise systématiquement un Stop-Loss</strong> (niveau où tu acceptes de couper ta perte).</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    <span><strong>Fixe un Take-Profit</strong> (niveau où tu encaisses tes gains).</span>
                  </li>
                </ul>
              </div>

              {/* Rule 2: Risk/Reward Ratio */}
              <div>
                <h3 className="font-bold mb-4 flex items-center gap-2">
                  <Percent className="w-5 h-5 text-primary" />
                  Ratio Risque/Gain (R:R)
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Compare combien tu risques de perdre vs combien tu peux gagner. <br/>
                  <strong>Formule :</strong> R:R = (Gain potentiel) / (Perte potentielle)
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <RatioCard 
                    ratio="1:1"
                    description="Tu risques 100$ pour gagner 100$"
                    verdict="INTERDIT"
                    color="red"
                  />
                  <RatioCard 
                    ratio="1:2.1"
                    description="Tu risques 100$ pour gagner 210$"
                    verdict="MINIMUM ABSOLU"
                    color="yellow"
                  />
                  <RatioCard 
                    ratio="1:3"
                    description="Tu risques 100$ pour gagner 300$"
                    verdict="Idéal Swing"
                    color="green"
                  />
                  <RatioCard 
                    ratio="1:5+"
                    description="Tu risques 100$ pour gagner 500$+"
                    verdict="Dynamique"
                    color="green"
                  />
                </div>
                
                <div className="mt-4 p-3 bg-red-500/10 rounded-lg border border-red-500/20">
                  <AlertTriangle className="w-4 h-4 text-red-400 inline mr-2" />
                  <span className="text-sm text-red-400">
                    <strong>RÈGLE ABSOLUE :</strong> Ne JAMAIS prendre un trade avec un R:R inférieur à 2.1:1. C'est la base de la rentabilité !
                  </span>
                </div>
                
                <div className="mt-2 p-3 bg-primary/10 rounded-lg border border-primary/20">
                  <Info className="w-4 h-4 text-primary inline mr-2" />
                  <span className="text-sm">
                    Avec un R:R de 2.1:1, même si tu ne gagnes que 35% de tes trades, tu restes rentable !
                  </span>
                </div>
              </div>

              {/* Rule 3: Position Size */}
              <div>
                <h3 className="font-bold mb-4 flex items-center gap-2">
                  <Calculator className="w-5 h-5 text-primary" />
                  Comment Calculer ta Taille de Position
                </h3>
                
                <div className="space-y-4">
                  <div className="p-4 bg-muted/30 rounded-lg border-l-4 border-blue-500">
                    <h4 className="font-semibold text-blue-400 mb-2">Étape 1 : Définir ton risque max</h4>
                    <p className="text-sm text-muted-foreground">
                      Décide combien tu acceptes de perdre au total. En général : <strong>1% du capital total</strong>.
                    </p>
                    <div className="mt-2 text-xs bg-background/50 p-2 rounded">
                      Capital = 52 000$ → Risque max = 520$
                    </div>
                  </div>

                  <div className="p-4 bg-muted/30 rounded-lg border-l-4 border-purple-500">
                    <h4 className="font-semibold text-purple-400 mb-2">Étape 2 : Calculer le risque par unité</h4>
                    <p className="text-sm text-muted-foreground">
                      C'est la différence entre ton prix d'entrée et ton Stop-Loss.
                    </p>
                    <div className="mt-2 text-xs bg-background/50 p-2 rounded">
                      Entrée ETH = 4 300$ | SL = 4 100$ → Risque/unité = 200$/ETH
                    </div>
                  </div>

                  <div className="p-4 bg-muted/30 rounded-lg border-l-4 border-green-500">
                    <h4 className="font-semibold text-green-400 mb-2">Étape 3 : Calculer la taille de position</h4>
                    <p className="text-sm text-muted-foreground mb-2">
                      <strong>Formule :</strong> Taille position = Risque total max / Risque par unité
                    </p>
                    <div className="mt-2 text-xs bg-background/50 p-2 rounded">
                      520$ / 200$ = <strong>2,6 ETH</strong> (≈ 11 180$) pour respecter ta règle de risque
                    </div>
                  </div>
                </div>
              </div>

              {/* Rule 4: Leverage */}
              <div className="mt-8 pt-8 border-t border-white/10">
                <h3 className="font-bold mb-4 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-yellow-400" />
                  L'Effet de Levier
                </h3>
                
                {/* Objectif */}
                <div className="p-4 bg-gradient-to-r from-yellow-500/10 to-transparent rounded-lg border border-yellow-500/20 mb-4">
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingUp className="w-5 h-5 text-yellow-400" />
                    <h4 className="font-bold text-yellow-400">Objectif</h4>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">
                    L'effet de levier permet de <strong>multiplier ton exposition au marché</strong> avec un capital réduit. 
                    Au lieu d'investir 10 000€ pour contrôler 10 000€ d'actifs, tu peux contrôler cette même position avec seulement 1 000€ de marge (levier x10).
                  </p>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      <span><strong>Amplifier les gains potentiels</strong> - Un mouvement de 5% devient 50% avec un levier x10</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      <span><strong>Trader avec un capital limité</strong> - Accéder à des positions importantes avec peu de fonds</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Wallet className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      <span><strong>Conserver de la liquidité</strong> - Contrairement au trading Spot où tout ton capital est bloqué, le levier te permet de garder des fonds disponibles pour d'autres opportunités ou pour renforcer une position</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      <span><strong>Diversifier</strong> - Ouvrir plusieurs positions simultanément avec le même capital</span>
                    </li>
                  </ul>
                </div>

                {/* Risques */}
                <div className="p-4 bg-gradient-to-r from-red-500/10 to-transparent rounded-lg border border-red-500/20 mb-4">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertTriangle className="w-5 h-5 text-red-400" />
                    <h4 className="font-bold text-red-400">Risques - Arme à Double Tranchant</h4>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">
                    Le levier amplifie les gains, <strong>mais aussi les pertes</strong>. C'est la raison principale des liquidations de comptes.
                  </p>
                  
                  {/* Table des impacts */}
                  <div className="overflow-x-auto mb-4">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left py-2 px-3 text-muted-foreground">Levier</th>
                          <th className="text-left py-2 px-3 text-muted-foreground">Mouvement prix</th>
                          <th className="text-left py-2 px-3 text-muted-foreground">Impact sur ta marge</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-white/5">
                          <td className="py-2 px-3 font-mono">x10</td>
                          <td className="py-2 px-3 text-green-400">+5%</td>
                          <td className="py-2 px-3 text-green-400 font-bold">+50% de gain</td>
                        </tr>
                        <tr className="border-b border-white/5">
                          <td className="py-2 px-3 font-mono">x10</td>
                          <td className="py-2 px-3 text-red-400">-5%</td>
                          <td className="py-2 px-3 text-red-400 font-bold">-50% de perte</td>
                        </tr>
                        <tr className="bg-red-500/10">
                          <td className="py-2 px-3 font-mono">x10</td>
                          <td className="py-2 px-3 text-red-400">-10%</td>
                          <td className="py-2 px-3 text-red-400 font-bold">-100% = LIQUIDATION</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="p-3 bg-red-500/20 rounded-lg border border-red-500/30">
                    <AlertTriangle className="w-4 h-4 text-red-400 inline mr-2" />
                    <span className="text-sm text-red-400">
                      <strong>DANGER - Liquidation :</strong> Avec un levier x10, une baisse de seulement 10% du prix suffit à perdre 100% de ta marge. Ta position est automatiquement fermée par l'exchange.
                    </span>
                  </div>
                </div>

                {/* Comment ça fonctionne */}
                <div className="p-4 bg-gradient-to-r from-blue-500/10 to-transparent rounded-lg border border-blue-500/20 mb-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Scale className="w-5 h-5 text-blue-400" />
                    <h4 className="font-bold text-blue-400">Comment ça Fonctionne ?</h4>
                  </div>
                  
                  <div className="bg-background/50 p-4 rounded-lg mb-4">
                    <h5 className="font-semibold mb-2 text-sm">Exemple concret :</h5>
                    <div className="text-xs text-muted-foreground space-y-1 font-mono">
                      <div>Capital disponible : <span className="text-white">1 000€</span></div>
                      <div>Levier utilisé : <span className="text-yellow-400">x10</span></div>
                      <div>Position totale contrôlée : <span className="text-primary">10 000€</span></div>
                    </div>
                  </div>

                  <div className="bg-background/50 p-4 rounded-lg mb-4">
                    <div className="text-xs space-y-2 font-mono">
                      <div className="text-muted-foreground">Tu achètes du BTC à 50 000€ avec 1 000€ de marge (x10)</div>
                      <div className="text-muted-foreground">→ Tu contrôles 0.2 BTC (valeur : 10 000€)</div>
                      <div className="border-t border-white/10 pt-2 mt-2"></div>
                      <div className="text-green-400">
                        Si BTC monte à 52 500€ (+5%) :<br/>
                        → Gain = 5% × 10 000€ = <strong>500€</strong><br/>
                        → ROI sur marge = 500€ / 1 000€ = <strong>+50%</strong>
                      </div>
                      <div className="border-t border-white/10 pt-2 mt-2"></div>
                      <div className="text-red-400">
                        Si BTC descend à 47 500€ (-5%) :<br/>
                        → Perte = 5% × 10 000€ = <strong>500€</strong><br/>
                        → ROI sur marge = -500€ / 1 000€ = <strong>-50%</strong>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <h5 className="font-semibold text-sm">Formules clés :</h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                      <div className="p-2 bg-background/50 rounded">
                        <span className="text-muted-foreground">Marge requise = </span>
                        <span className="font-mono">Position / Levier</span>
                      </div>
                      <div className="p-2 bg-background/50 rounded">
                        <span className="text-muted-foreground">Liquidation LONG = </span>
                        <span className="font-mono">Entry × (1 - 1/Levier)</span>
                      </div>
                      <div className="p-2 bg-background/50 rounded">
                        <span className="text-muted-foreground">Liquidation SHORT = </span>
                        <span className="font-mono">Entry × (1 + 1/Levier)</span>
                      </div>
                      <div className="p-2 bg-background/50 rounded">
                        <span className="text-muted-foreground">ROI = </span>
                        <span className="font-mono">Mouvement% × Levier</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Recommandations */}
                <div className="p-4 bg-gradient-to-r from-green-500/10 to-transparent rounded-lg border border-green-500/20">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    <h4 className="font-bold text-green-400">Recommandations TRADEX</h4>
                  </div>
                  
                  <div className="overflow-x-auto mb-4">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left py-2 px-3 text-muted-foreground">Profil</th>
                          <th className="text-left py-2 px-3 text-muted-foreground">Levier max recommandé</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-white/5">
                          <td className="py-2 px-3">Débutant</td>
                          <td className="py-2 px-3 font-mono text-yellow-400">x2 à x5</td>
                        </tr>
                        <tr className="border-b border-white/5">
                          <td className="py-2 px-3">Intermédiaire</td>
                          <td className="py-2 px-3 font-mono text-yellow-400">x5 à x10</td>
                        </tr>
                        <tr className="border-b border-white/5">
                          <td className="py-2 px-3">Avancé</td>
                          <td className="py-2 px-3 font-mono text-orange-400">x10 à x20</td>
                        </tr>
                        <tr>
                          <td className="py-2 px-3">Expert</td>
                          <td className="py-2 px-3 font-mono text-red-400">x20+ (SL très serré)</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="space-y-2">
                    <h5 className="font-semibold text-sm mb-2">Les 3 règles essentielles :</h5>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-start gap-2">
                        <span className="text-primary font-bold">1.</span>
                        <span><strong>TOUJOURS</strong> placer un Stop-Loss AVANT le niveau de liquidation</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="text-primary font-bold">2.</span>
                        <span><strong>NE JAMAIS</strong> risquer plus de 1-2% de ton capital par trade</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="text-primary font-bold">3.</span>
                        <span><strong>RÉDUIRE</strong> le levier quand le marché est volatil</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </SectionCard>
        </TabsContent>

        {/* ===== LEXIQUE ===== */}
        <TabsContent value="lexicon" className="mt-0">
          <SectionCard icon={BookMarked} title="Lexique du Trading" color="text-blue-400" bgColor="bg-blue-500/10">
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="support-resistance">
                <AccordionTrigger className="text-left">
                  <span className="flex items-center gap-2">
                    <TrendingDown className="w-4 h-4 text-green-400" />
                    Support & Résistance
                  </span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4">
                    <DefinitionItem
                      term="Support"
                      definition="Zone où le prix a rebondi plusieurs fois à la hausse. C'est une possible zone d'achat."
                      example="Bitcoin a rebondi 3 fois sur les 60 000$. Ce niveau est un support fort."
                      type="Zone d'achat"
                    />
                    <DefinitionItem
                      term="Résistance"
                      definition="Zone où le prix a bloqué plusieurs fois à la hausse. C'est une possible zone de vente."
                      example="ETH n'arrive pas à dépasser 4 500$ depuis 2 semaines. C'est une résistance."
                      type="Zone de vente"
                    />
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="sl-tp">
                <AccordionTrigger className="text-left">
                  <span className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-red-400" />
                    Stop-Loss & Take-Profit
                  </span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4">
                    <DefinitionItem
                      term="Stop-Loss (SL)"
                      definition="Niveau de prix où tu acceptes de couper ta perte si le marché va contre toi. C'est ton parachute : il t'empêche de perdre trop."
                      example="J'achète ETH à 4 300$. Je mets un SL à 4 100$ → si ça tombe là, je sors avec une perte contrôlée de 200$/ETH."
                      type="Protection"
                    />
                    <DefinitionItem
                      term="Take-Profit (TP)"
                      definition="Niveau de prix où tu encaisses tes gains automatiquement. Cela t'évite de reperdre après une hausse."
                      example="J'achète ETH à 4 300$. Je mets un TP à 4 700$ → si ça monte là, mes gains de 400$/ETH sont sécurisés."
                      type="Objectif"
                    />
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="capital">
                <AccordionTrigger className="text-left">
                  <span className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-yellow-400" />
                    Capital & Position
                  </span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4">
                    <DefinitionItem
                      term="Capital"
                      definition="Le montant total de ton portefeuille. Il sert de base pour calculer ton risque max par trade."
                      example="Mon capital est de 52 000$. Je ne risque que 1% soit 520$ par trade."
                      type="Base"
                    />
                    <DefinitionItem
                      term="Taille de Position"
                      definition="Nombre de coins/actions que tu achètes. Doit être calculé en fonction de ton risque max, pas au hasard !"
                      example="Si ETH = 4 300$ et j'achète pour 11 180$, alors ma taille = 2,6 ETH"
                      type="Calcul"
                    />
                    <DefinitionItem
                      term="Risque par Trade"
                      definition="Pourcentage de ton capital que tu acceptes de perdre au maximum sur un trade. Généralement entre 1% et 2%."
                      example="Capital 52 000$ × 1% risque = 520$ maximum de perte par trade"
                      type="Règle"
                    />
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="leverage">
                <AccordionTrigger className="text-left">
                  <span className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-orange-400" />
                    Levier & Liquidation
                  </span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4">
                    <DefinitionItem
                      term="Levier (Leverage)"
                      definition="Multiplicateur qui amplifie tes gains ET tes pertes. Un levier 10× signifie que 1% de mouvement = 10% de ton capital."
                      example="Avec 1000$ et levier 10×, tu contrôles 10 000$ de position. Si le prix bouge de 1%, tu gagnes/perds 100$ (10% de ta marge)."
                      type="Futures"
                    />
                    <DefinitionItem
                      term="Prix de Liquidation"
                      definition="Prix auquel tu perds 100% de ta marge (ton collatéral). Si le marché atteint ce prix, ta position est fermée automatiquement."
                      example="LONG BTC à 0,040$ avec levier 10× → Liquidation ≈ 0,036$. Place ton SL AVANT ce niveau !"
                      type="Danger"
                    />
                    <div className="p-3 bg-red-500/10 rounded-lg border border-red-500/20 mt-4">
                      <AlertTriangle className="w-4 h-4 text-red-400 inline mr-2" />
                      <span className="text-sm text-red-400">
                        Avec un levier élevé, ton SL doit être bien plus serré que ton prix de liquidation pour te protéger !
                      </span>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </SectionCard>
        </TabsContent>

        {/* ===== INDICATEURS ===== */}
        <TabsContent value="indicators" className="mt-0">
          <SectionCard icon={Activity} title="Indicateurs Techniques" color="text-purple-400" bgColor="bg-purple-500/10">
            <div className="space-y-6">
              
              {/* MA/EMA - Section complète */}
              <div className="p-4 bg-muted/30 rounded-lg border-l-4 border-blue-500">
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp className="w-5 h-5 text-blue-400" />
                  <h3 className="font-bold text-lg">1. Moyennes Mobiles (MA / EMA)</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  Ce sont des lignes qui <strong>lissent le prix</strong> sur une période donnée.
                  Ex : une MA 50 = moyenne des 50 dernières bougies.
                </p>
                
                {/* Interprétation */}
                <div className="mb-4">
                  <h4 className="font-semibold text-sm mb-2 text-blue-400">Identifier la tendance :</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="p-3 bg-green-500/10 rounded border border-green-500/20">
                      <ArrowUpRight className="w-4 h-4 text-green-400 inline mr-2" />
                      <span className="text-sm">Prix <strong>&gt;</strong> MA</span>
                      <p className="text-xs text-green-400 mt-1">Tendance haussière</p>
                    </div>
                    <div className="p-3 bg-red-500/10 rounded border border-red-500/20">
                      <ArrowDownRight className="w-4 h-4 text-red-400 inline mr-2" />
                      <span className="text-sm">Prix <strong>&lt;</strong> MA</span>
                      <p className="text-xs text-red-400 mt-1">Tendance baissière</p>
                    </div>
                  </div>
                </div>

                {/* Croisements */}
                <div className="mb-4">
                  <h4 className="font-semibold text-sm mb-2 text-blue-400">Les croisements de MA (signaux importants) :</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="p-3 bg-green-500/10 rounded border border-green-500/20">
                      <div className="font-semibold text-green-400 mb-1">Golden Cross</div>
                      <p className="text-xs text-muted-foreground">
                        MA50 croise MA200 <strong>par le haut</strong> → Signal bullish (achat)
                      </p>
                    </div>
                    <div className="p-3 bg-red-500/10 rounded border border-red-500/20">
                      <div className="font-semibold text-red-400 mb-1">Death Cross</div>
                      <p className="text-xs text-muted-foreground">
                        MA50 croise MA200 <strong>par le bas</strong> → Signal bearish (vente)
                      </p>
                    </div>
                  </div>
                </div>

                {/* Exemple pratique */}
                <div className="p-3 bg-primary/10 rounded-lg border border-primary/20">
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="w-4 h-4 text-primary" />
                    <span className="font-semibold text-sm">Exemple pratique :</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Tu peux trader dans le sens de la <strong>MA200 en 1D</strong> pour suivre la tendance globale.
                    Si le prix est au-dessus de la MA200 → <span className="text-green-400">tendance haussière forte</span>.
                  </p>
                </div>
              </div>

              {/* RSI - Section complète */}
              <div className="p-4 bg-muted/30 rounded-lg border-l-4 border-purple-500">
                <div className="flex items-center gap-2 mb-3">
                  <Activity className="w-5 h-5 text-purple-400" />
                  <h3 className="font-bold text-lg">2. RSI (Relative Strength Index)</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  Oscillateur entre 0 et 100. Indique si un actif est <strong>suracheté</strong> ou <strong>survendu</strong>.
                </p>

                {/* Zones RSI */}
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="p-3 bg-red-500/10 rounded text-center border border-red-500/20">
                    <div className="text-2xl font-bold text-red-400">&gt; 70</div>
                    <div className="text-xs font-semibold text-red-400">SURACHAT</div>
                    <div className="text-xs text-muted-foreground mt-1">Risque de correction</div>
                  </div>
                  <div className="p-3 bg-yellow-500/10 rounded text-center border border-yellow-500/20">
                    <div className="text-2xl font-bold text-yellow-400">30-70</div>
                    <div className="text-xs font-semibold text-yellow-400">NEUTRE</div>
                    <div className="text-xs text-muted-foreground mt-1">Zone d'équilibre</div>
                  </div>
                  <div className="p-3 bg-green-500/10 rounded text-center border border-green-500/20">
                    <div className="text-2xl font-bold text-green-400">&lt; 30</div>
                    <div className="text-xs font-semibold text-green-400">SURVENTE</div>
                    <div className="text-xs text-muted-foreground mt-1">Risque de rebond</div>
                  </div>
                </div>

                {/* Divergences */}
                <div className="p-3 bg-orange-500/10 rounded-lg border border-orange-500/20">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-4 h-4 text-orange-400" />
                    <span className="font-semibold text-sm text-orange-400">Divergences RSI</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Si le <strong>prix fait un plus haut</strong> mais <strong>le RSI baisse</strong> 
                    → la tendance s'essouffle, retournement possible.
                  </p>
                  <div className="mt-2 text-xs text-orange-400">
                    C'est un signal avancé de faiblesse de la tendance !
                  </div>
                </div>
              </div>

              {/* MACD - Section complète */}
              <div className="p-4 bg-muted/30 rounded-lg border-l-4 border-cyan-500">
                <div className="flex items-center gap-2 mb-3">
                  <BarChart3 className="w-5 h-5 text-cyan-400" />
                  <h3 className="font-bold text-lg">3. MACD (Moving Average Convergence Divergence)</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  Mesure la <strong>vitesse et le sens d'évolution</strong> d'un actif. 
                  Traduit la tendance et la force du prix.
                </p>

                {/* Composants MACD */}
                <div className="mb-4">
                  <h4 className="font-semibold text-sm mb-2 text-cyan-400">Les 3 composants :</h4>
                  <div className="space-y-2">
                    <div className="p-2 bg-background/50 rounded flex items-start gap-2">
                      <div className="w-3 h-3 rounded-full bg-cyan-400 mt-1 flex-shrink-0"></div>
                      <div>
                        <span className="font-semibold text-sm">MACD Line (courbe rapide)</span>
                        <p className="text-xs text-muted-foreground">Différence entre EMA12 et EMA26</p>
                      </div>
                    </div>
                    <div className="p-2 bg-background/50 rounded flex items-start gap-2">
                      <div className="w-3 h-3 rounded-full bg-orange-400 mt-1 flex-shrink-0"></div>
                      <div>
                        <span className="font-semibold text-sm">Signal Line (courbe lente)</span>
                        <p className="text-xs text-muted-foreground">EMA9 de la MACD line</p>
                      </div>
                    </div>
                    <div className="p-2 bg-background/50 rounded flex items-start gap-2">
                      <div className="w-3 h-3 rounded-full bg-gray-400 mt-1 flex-shrink-0"></div>
                      <div>
                        <span className="font-semibold text-sm">Histogramme</span>
                        <p className="text-xs text-muted-foreground">Distance entre MACD line et Signal line</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Signaux MACD */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                  <div className="p-3 bg-green-500/10 rounded border border-green-500/20">
                    <div className="flex items-center gap-2 mb-2">
                      <ArrowUpRight className="w-4 h-4 text-green-400" />
                      <span className="font-semibold text-green-400">Croisement Haussier</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      MACD line passe <strong>au-dessus</strong> de la Signal line.<br/>
                      → Le momentum acheteur prend le dessus.<br/>
                      → Signal d'achat ou reprise de tendance haussière.
                    </p>
                  </div>
                  <div className="p-3 bg-red-500/10 rounded border border-red-500/20">
                    <div className="flex items-center gap-2 mb-2">
                      <ArrowDownRight className="w-4 h-4 text-red-400" />
                      <span className="font-semibold text-red-400">Croisement Baissier</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      MACD line passe <strong>en dessous</strong> de la Signal line.<br/>
                      → Momentum vendeur.<br/>
                      → Signal de vente ou correction à venir.
                    </p>
                  </div>
                </div>

                {/* Divergence MACD */}
                <div className="p-3 bg-purple-500/10 rounded-lg border border-purple-500/20 mb-4">
                  <div className="font-semibold text-sm text-purple-400 mb-1">Divergence MACD/Prix</div>
                  <p className="text-xs text-muted-foreground">
                    = Consolidation due à une <strong>perte de force</strong> (acheteuse ou vendeuse) 
                    avec un potentiel break dans le sens inverse du mouvement principal.
                  </p>
                </div>

                {/* Avertissement */}
                <div className="p-3 bg-red-500/10 rounded-lg border border-red-500/20">
                  <AlertTriangle className="w-4 h-4 text-red-400 inline mr-2" />
                  <span className="font-bold text-red-400">ATTENTION :</span>
                  <span className="text-sm text-red-400"> Ne JAMAIS utiliser le MACD seul ! Beaucoup de faux signaux en range.</span>
                </div>
              </div>

              {/* VOLUMES - Nouvelle section */}
              <div className="p-4 bg-muted/30 rounded-lg border-l-4 border-yellow-500">
                <div className="flex items-center gap-2 mb-3">
                  <BarChart3 className="w-5 h-5 text-yellow-400" />
                  <h3 className="font-bold text-lg">4. Volumes</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  Mesurent la <strong>quantité d'échanges</strong> (nombre de contrats ou tokens échangés).
                  Un mouvement de prix avec <strong>fort volume = beaucoup plus fiable</strong>.
                </p>

                {/* Exemples volumes */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                  <div className="p-3 bg-green-500/10 rounded border border-green-500/20">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      <span className="font-semibold text-green-400">Signal Valide</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Cassure d'une résistance avec <strong>fort volume</strong>.<br/>
                      → Le mouvement est confirmé par les acheteurs.
                    </p>
                  </div>
                  <div className="p-3 bg-red-500/10 rounded border border-red-500/20">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-4 h-4 text-red-400" />
                      <span className="font-semibold text-red-400">Faux Breakout</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Cassure avec <strong>faible volume</strong>.<br/>
                      → Risque de retour dans le range (fake breakout).
                    </p>
                  </div>
                </div>

                <div className="p-3 bg-primary/10 rounded-lg border border-primary/20">
                  <Info className="w-4 h-4 text-primary inline mr-2" />
                  <span className="text-sm">
                    <strong>Règle :</strong> Toujours vérifier le volume avant de valider un signal technique !
                  </span>
                </div>
              </div>

              {/* Section Utilisation Pratique */}
              <div className="p-4 bg-gradient-to-r from-primary/10 to-purple-500/10 rounded-lg border border-primary/30">
                <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5 text-primary" />
                  Comment utiliser ces indicateurs ?
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Swing Trading */}
                  <div className="p-4 bg-background/50 rounded-lg">
                    <h4 className="font-semibold text-yellow-400 mb-3">Swing Trading (Range)</h4>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                        <span>Trade les retours vers les <strong>moyennes clés</strong> (ex: rebond sur MA50)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                        <span>Utilise le <strong>RSI</strong> pour détecter les zones extrêmes (surachat/survente)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                        <span>Achète au support, vends à la résistance</span>
                      </li>
                    </ul>
                  </div>

                  {/* Trend Following */}
                  <div className="p-4 bg-background/50 rounded-lg">
                    <h4 className="font-semibold text-green-400 mb-3">Trend Following</h4>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                        <span>Base ta stratégie sur les <strong>croisements MA</strong> ou breakouts au-dessus de MA200</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                        <span>Utilise le <strong>MACD</strong> pour confirmer un momentum soutenu</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                        <span>Vérifie toujours le <strong>volume</strong> pour valider les cassures</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </SectionCard>
        </TabsContent>

        {/* ===== MOUVEMENTS DE MARCHÉ (ICT) ===== */}
        <TabsContent value="market" className="mt-0">
          {/* ===== HORAIRES DE TRADING ===== */}
          <SectionCard icon={Clock} title={language === 'fr' ? "Horaires de Trading" : "Trading Hours"} color="text-yellow-400" bgColor="bg-yellow-500/10">
            <p className="text-sm text-muted-foreground mb-4">
              {language === 'fr' 
                ? "Les trades ne sont clôturés que lorsque le marché correspondant est ouvert. Voici les horaires par type d'actif :"
                : "Trades are only closed when the corresponding market is open. Here are the hours by asset type:"}
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* US Stocks */}
              <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/20">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg">🇺🇸</span>
                  <span className="font-bold text-blue-400">{language === 'fr' ? "Actions US" : "US Stocks"}</span>
                </div>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{language === 'fr' ? "Ouverture" : "Open"}</span>
                    <span className="font-mono text-blue-300">15:30 Paris</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{language === 'fr' ? "Fermeture" : "Close"}</span>
                    <span className="font-mono text-blue-300">22:00 Paris</span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">AAPL, MSFT, AMZN, GOOGL, META, NVDA, TSLA...</p>
              </div>

              {/* European Stocks */}
              <div className="p-4 bg-indigo-500/10 rounded-lg border border-indigo-500/20">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg">🇪🇺</span>
                  <span className="font-bold text-indigo-400">{language === 'fr' ? "Actions Européennes" : "European Stocks"}</span>
                </div>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{language === 'fr' ? "Ouverture" : "Open"}</span>
                    <span className="font-mono text-indigo-300">09:00 Paris</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{language === 'fr' ? "Fermeture" : "Close"}</span>
                    <span className="font-mono text-indigo-300">17:30 Paris</span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">TTE, MC.PA, AIR, LVMH, BNP...</p>
              </div>

              {/* Crypto */}
              <div className="p-4 bg-green-500/10 rounded-lg border border-green-500/20">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg">🪙</span>
                  <span className="font-bold text-green-400">Crypto</span>
                </div>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Statut</span>
                    <span className="font-mono text-green-300">24/7</span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">BTC, ETH, SOL, XRP, AVAX, DOT, LINK...</p>
              </div>

              {/* Gold & Commodities */}
              <div className="p-4 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg">🏆</span>
                  <span className="font-bold text-yellow-400">{language === 'fr' ? "Or & Matières Premières" : "Gold & Commodities"}</span>
                </div>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Statut</span>
                    <span className="font-mono text-yellow-300">{language === 'fr' ? "~24h (Lun-Ven)" : "~24h (Mon-Fri)"}</span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">GC=F (Or), CL=F (Pétrole), SI=F (Argent)...</p>
              </div>

              {/* Forex */}
              <div className="p-4 bg-purple-500/10 rounded-lg border border-purple-500/20">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg">💱</span>
                  <span className="font-bold text-purple-400">Forex</span>
                </div>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{language === 'fr' ? "Ouverture" : "Open"}</span>
                    <span className="font-mono text-purple-300">{language === 'fr' ? "Dim 23:00" : "Sun 11:00 PM"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{language === 'fr' ? "Fermeture" : "Close"}</span>
                    <span className="font-mono text-purple-300">{language === 'fr' ? "Ven 23:00" : "Fri 11:00 PM"}</span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">EUR/USD, GBP/USD, USD/JPY...</p>
              </div>

              {/* Weekend Info */}
              <div className="p-4 bg-red-500/10 rounded-lg border border-red-500/20">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg">📅</span>
                  <span className="font-bold text-red-400">{language === 'fr' ? "Week-end" : "Weekend"}</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  {language === 'fr' 
                    ? "Seules les cryptos sont tradables le week-end. Les autres marchés sont fermés du vendredi soir au dimanche soir."
                    : "Only crypto is tradable on weekends. Other markets are closed from Friday evening to Sunday evening."}
                </p>
              </div>
            </div>

            <div className="mt-4 p-3 bg-primary/10 rounded-lg border border-primary/30">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                <p className="text-sm text-muted-foreground">
                  {language === 'fr' 
                    ? "Les trades ne sont automatiquement clôturés (SL/TP) que lorsque le marché est ouvert. Cela évite les fausses clôtures basées sur des prix obsolètes."
                    : "Trades are only automatically closed (SL/TP) when the market is open. This prevents false closures based on stale prices."}
                </p>
              </div>
            </div>
          </SectionCard>

          <SectionCard icon={TrendingUp} title="Structures de Marché (ICT)" color="text-cyan-400" bgColor="bg-cyan-500/10">
            <Accordion type="multiple" className="w-full space-y-2">
              
              {/* ===== FVG - FAIR VALUE GAP ===== */}
              <AccordionItem value="fvg" className="border border-cyan-500/20 rounded-lg px-4">
                <AccordionTrigger className="text-left hover:no-underline">
                  <span className="flex items-center gap-2 font-bold text-cyan-400">
                    <BarChart3 className="w-5 h-5" />
                    Fair Value Gap (FVG)
                  </span>
                </AccordionTrigger>
                <AccordionContent className="space-y-4 pt-2">
                  <p className="text-sm text-muted-foreground">
                    <strong>= Déséquilibre entre offre et demande</strong>
                  </p>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-green-500/10 rounded border border-green-500/20">
                      <div className="font-semibold text-green-400 text-sm">FVG Haussier</div>
                      <p className="text-xs text-muted-foreground mt-1">Plus d'acheteurs que de vendeurs</p>
                    </div>
                    <div className="p-3 bg-red-500/10 rounded border border-red-500/20">
                      <div className="font-semibold text-red-400 text-sm">FVG Baissier</div>
                      <p className="text-xs text-muted-foreground mt-1">Plus de vendeurs que d'acheteurs</p>
                    </div>
                  </div>

                  <div className="p-3 bg-muted/30 rounded-lg">
                    <h5 className="font-semibold text-sm mb-2 text-primary">Comment identifier une FVG ?</h5>
                    <p className="text-xs text-muted-foreground mb-2">Il faut <strong>3 bougies</strong> :</p>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      <li>• <strong>Haussier</strong> : Haut de la bougie 1 et Bas de la bougie 3</li>
                      <li>• <strong>Baissier</strong> : Bas de la bougie 1 et Haut de la bougie 3</li>
                    </ul>
                  </div>

                  <div className="p-3 bg-purple-500/10 rounded-lg border border-purple-500/20">
                    <h5 className="font-semibold text-sm mb-2 text-purple-400">Types particuliers de FVG :</h5>
                    <div className="space-y-2 text-xs">
                      <div><strong className="text-purple-400">BISI</strong> : 3 bougies haussières avec la 2ème très très haussière (la 3ème mèche ne vient presque pas dans le corps de la 2ème)</div>
                      <div><strong className="text-purple-400">SIBI</strong> : Inverse du BISI (3 bougies baissières)</div>
                      <div><strong className="text-orange-400">Breakaway Gap (BAG)</strong> : FVG qui ne revient pas être comblée. Directement suivie d'une autre FVG → la 2ème a de fortes chances d'être comblée. Annonciateur de continuation ++</div>
                    </div>
                  </div>

                  <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
                    <h5 className="font-semibold text-sm mb-2 text-blue-400">Seuil médian de la FVG</h5>
                    <p className="text-xs text-muted-foreground">
                      On tire un rectangle au niveau de la FVG puis le seuil médian :<br/>
                      • Clôture en dessous (baissier) = probabilité continuation baissière<br/>
                      • Clôture au dessus = on casse et on part à la hausse<br/>
                      • Mini touchette sur la FVG = signe de continuation
                    </p>
                  </div>

                  <div className="p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
                    <h5 className="font-semibold text-sm mb-2 text-yellow-400">Exemples de prise de décision :</h5>
                    <ol className="text-xs text-muted-foreground space-y-2">
                      <li><strong>1)</strong> Le marché monte avec FVG haussiers et descend sans FVG baissiers → peu de chance de retournement</li>
                      <li><strong>2)</strong> FVG haussiers et baissiers jamais maintenus = consolidation avant manipulation et expansion</li>
                      <li><strong>3)</strong> FVG bullish non maintenue devient un POI pour retracement haussier sur tendance baissière globale</li>
                      <li><strong>4)</strong> Touchette dans un FVG = continuation + souvent on revient la retoucher après</li>
                    </ol>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* ===== IFVG & BPR ===== */}
              <AccordionItem value="ifvg" className="border border-orange-500/20 rounded-lg px-4">
                <AccordionTrigger className="text-left hover:no-underline">
                  <span className="flex items-center gap-2 font-bold text-orange-400">
                    <ArrowDownRight className="w-5 h-5" />
                    Inverted FVG (IFVG) & BPR
                  </span>
                </AccordionTrigger>
                <AccordionContent className="space-y-4 pt-2">
                  <div className="p-3 bg-orange-500/10 rounded-lg border border-orange-500/20">
                    <h5 className="font-semibold text-sm mb-2 text-orange-400">IFVG (Inverted Fair Value Gap)</h5>
                    <p className="text-xs text-muted-foreground">
                      = Quand le marché <strong>casse une FVG haussière à la baisse</strong> (ou l'inverse)<br/>
                      C'est un signe <strong>+++ de retournement de tendance</strong> pour aller chercher les POI (zones de stop)
                    </p>
                  </div>

                  <div className="p-3 bg-red-500/10 rounded-lg border border-red-500/20">
                    <h5 className="font-semibold text-sm mb-2 text-red-400">BPR (Balance Price Range)</h5>
                    <p className="text-xs text-muted-foreground">
                      = Cassure d'une ancienne FVG par une nouvelle FVG<br/>
                      On vient souvent retravailler cette nouvelle zone (avec le concept de seuil médian)
                    </p>
                    <div className="mt-2 p-2 bg-background/50 rounded text-xs">
                      <strong>Trade :</strong> Soit entrée à la clôture de la 3ème bougie (celle qui crée le BPR), soit attendre un retest de la FVG précédente
                    </div>
                  </div>

                  <div className="p-2 bg-yellow-500/10 rounded text-xs border border-yellow-500/20">
                    <AlertTriangle className="w-3 h-3 text-yellow-400 inline mr-1" />
                    <strong>Attention :</strong> Une IFVG ne devient pas forcément un BPR
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* ===== IRI & IMBALANCE ===== */}
              <AccordionItem value="iri" className="border border-purple-500/20 rounded-lg px-4">
                <AccordionTrigger className="text-left hover:no-underline">
                  <span className="flex items-center gap-2 font-bold text-purple-400">
                    <Activity className="w-5 h-5" />
                    IRI & Imbalance
                  </span>
                </AccordionTrigger>
                <AccordionContent className="space-y-4 pt-2">
                  <div className="p-3 bg-purple-500/10 rounded-lg border border-purple-500/20">
                    <h5 className="font-semibold text-sm mb-2 text-purple-400">IRI (Immediate Re-Imbalance)</h5>
                    <p className="text-xs text-muted-foreground">
                      = Presque une FVG sauf que la <strong>mèche de la 3ème bougie touche la 1ère</strong><br/>
                      <strong>Interprétation :</strong> Fort signal de continuation si maintenue
                    </p>
                    <div className="mt-2 p-2 bg-background/50 rounded text-xs">
                      <strong>Trade :</strong> IRI qui fail + OB à la suite = probabilité de retournement ++
                    </div>
                  </div>

                  <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
                    <h5 className="font-semibold text-sm mb-2 text-blue-400">Imbalance</h5>
                    <p className="text-xs text-muted-foreground">
                      = Une bougie avec un <strong>corps très petit</strong> et des <strong>énormes mèches</strong> au nord et au sud<br/>
                      Le prix va souvent venir retravailler ces mèches
                    </p>
                    <div className="mt-2 p-2 bg-background/50 rounded text-xs">
                      <AlertTriangle className="w-3 h-3 text-yellow-400 inline mr-1" />
                      <strong>Attention :</strong> On prend des bougies qui ont entraîné un retournement
                    </div>
                    <div className="mt-2 p-2 bg-background/50 rounded text-xs">
                      <strong>Trade :</strong> Retester le seuil médian d'une mèche = prise de position dans le sens du SD pour aller chercher un POI
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* ===== POI - POINTS D'INTÉRÊT ===== */}
              <AccordionItem value="poi" className="border border-yellow-500/20 rounded-lg px-4">
                <AccordionTrigger className="text-left hover:no-underline">
                  <span className="flex items-center gap-2 font-bold text-yellow-400">
                    <Target className="w-5 h-5" />
                    POI (Points d'Intérêt)
                  </span>
                </AccordionTrigger>
                <AccordionContent className="space-y-4 pt-2">
                  <p className="text-sm text-muted-foreground">
                    Le marché a pour objectif de <strong>chercher les liquidités au maximum</strong>. C'est comme ça que le prix se dirige.
                    Lors d'un retournement, on cherche un POI pour anticiper où le prix peut se diriger.
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="p-3 bg-red-500/10 rounded border border-red-500/20">
                      <div className="font-semibold text-red-400 text-sm">BSL (Buy-Side Liquidity)</div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Au nord du range = liquidation des SL des vendeurs
                      </p>
                    </div>
                    <div className="p-3 bg-green-500/10 rounded border border-green-500/20">
                      <div className="font-semibold text-green-400 text-sm">SSL (Sell-Side Liquidity)</div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Au sud du range = liquidation des SL des acheteurs
                      </p>
                    </div>
                  </div>

                  <div className="p-3 bg-muted/30 rounded-lg">
                    <h5 className="font-semibold text-sm mb-2">Types de POI :</h5>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      <li>• <strong>EQL / EQH</strong> : Equal Low / Equal High - mèches au même endroit</li>
                      <li>• <strong>RQL / RQH</strong> : Relative Equal Low/High - mèches presque au même endroit</li>
                      <li>• <strong>ML / MH</strong> : Monday Low / High - le marché punit souvent les traders du début de semaine</li>
                      <li>• <strong>PDL / PDH</strong> : Previous Daily Low / High</li>
                      <li>• <strong>PWL / PWH</strong> : Previous Weekly Low / High</li>
                      <li>• <strong>PML / PMH</strong> : Previous Monthly Low / High</li>
                    </ul>
                  </div>

                  <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/20">
                    <h5 className="font-semibold text-sm mb-2 text-green-400">POI puissants (+++)</h5>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      <li>• POI qui se combinent (ex: PWL + FVG)</li>
                      <li>• Plus l'unité de temps est grande, plus c'est important</li>
                      <li>• Low/High qui couvre une FVG, IFVG, BPR, BAG</li>
                    </ul>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* ===== BOUGIES ===== */}
              <AccordionItem value="candles" className="border border-pink-500/20 rounded-lg px-4">
                <AccordionTrigger className="text-left hover:no-underline">
                  <span className="flex items-center gap-2 font-bold text-pink-400">
                    <BarChart3 className="w-5 h-5" />
                    Les Bougies & Bougies Vectorielles
                  </span>
                </AccordionTrigger>
                <AccordionContent className="space-y-4 pt-2">
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <h5 className="font-semibold text-sm mb-2">Règles sur les bougies :</h5>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      <li>• Pour identifier un OB : regarder les <strong>corps</strong>, pas les mèches</li>
                      <li>• Les mèches = prises de liquidité (cherchent les stops)</li>
                      <li>• Le <strong>seuil médian des mèches</strong> est un POI important</li>
                    </ul>
                  </div>

                  <div className="p-3 bg-pink-500/10 rounded-lg border border-pink-500/20">
                    <h5 className="font-semibold text-sm mb-2 text-pink-400">Reversal Candle</h5>
                    <p className="text-xs text-muted-foreground">
                      Points de retournement<br/>
                      • <strong>En rally haussier</strong> : clôture avec corps sous le plus bas de la dernière bougie haussière<br/>
                      • Si en plus elle crée une FVG → signe de retournement<br/>
                      • Peut avoir 0 ou plusieurs bougies intermédiaires<br/>
                      • Crée un <strong>M</strong> en bearish ou <strong>W</strong> en bullish
                    </p>
                  </div>

                  <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
                    <h5 className="font-semibold text-sm mb-2 text-blue-400">Candle Range Theory</h5>
                    <p className="text-xs text-muted-foreground">
                      Prise d'une extrémité → très probable d'aller chercher l'autre extrémité
                    </p>
                    <div className="mt-2 p-2 bg-background/50 rounded text-xs">
                      <AlertTriangle className="w-3 h-3 text-yellow-400 inline mr-1" />
                      On prend des bougies qui couvrent une FVG et des bougies très larges
                    </div>
                  </div>

                  <div className="p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
                    <h5 className="font-semibold text-sm mb-2 text-yellow-400">Bougies Vectorielles</h5>
                    <p className="text-xs text-muted-foreground">
                      = Bougies avec les <strong>plus gros volumes des 12 derniers jours</strong><br/>
                      • Celles avec FVG sont pertinentes ++<br/>
                      • Reversal candle vectorielle = très puissant<br/>
                      • Souvent positionnées lors des tops ou bottoms
                    </p>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* ===== DYNAMIQUE DE PRIX ===== */}
              <AccordionItem value="dynamics" className="border border-green-500/20 rounded-lg px-4">
                <AccordionTrigger className="text-left hover:no-underline">
                  <span className="flex items-center gap-2 font-bold text-green-400">
                    <TrendingUp className="w-5 h-5" />
                    Dynamique de Prix
                  </span>
                </AccordionTrigger>
                <AccordionContent className="space-y-4 pt-2">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-green-500/10 rounded border border-green-500/20">
                      <div className="font-semibold text-green-400 text-sm">Haussière</div>
                      <p className="text-xs text-muted-foreground mt-1">Sommets de + en + hauts et creux pareil</p>
                    </div>
                    <div className="p-3 bg-red-500/10 rounded border border-red-500/20">
                      <div className="font-semibold text-red-400 text-sm">Baissière</div>
                      <p className="text-xs text-muted-foreground mt-1">Inverse</p>
                    </div>
                  </div>

                  <div className="p-3 bg-muted/30 rounded-lg">
                    <h5 className="font-semibold text-sm mb-2">Sommets & Creux :</h5>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      <li>• <strong>Sommets majeurs</strong> : dernier sommet qui a été cassé</li>
                      <li>• <strong>Creux majeurs</strong> : dernier creux avant cassage du sommet</li>
                      <li>• Points importants : même si tendance inverse à CT, on ne devrait pas les retoucher</li>
                      <li>• Sommets intermédiaires : peuvent être des prises de liquidités</li>
                    </ul>
                  </div>

                  <div className="p-3 bg-red-500/10 rounded-lg border border-red-500/20">
                    <AlertTriangle className="w-4 h-4 text-red-400 inline mr-2" />
                    <span className="text-xs text-red-400">
                      <strong>ATTENTION :</strong> Ne pas mettre de SL sous les creux intermédiaires (risque de prise de liquidité)
                    </span>
                  </div>

                  <div className="p-3 bg-cyan-500/10 rounded-lg border border-cyan-500/20">
                    <h5 className="font-semibold text-sm mb-2 text-cyan-400">HRLR (High Resistance Liquidity Run)</h5>
                    <p className="text-xs text-muted-foreground mb-2">
                      <strong>= Zone de prix évidente pour une prise de liquidité PUISSANTE</strong>
                    </p>
                    <div className="space-y-2 text-xs">
                      <div className="p-2 bg-background/50 rounded">
                        <strong className="text-cyan-400">Identification 1 :</strong> Prise de liquidité + hausse très rapide. On ne laisse PAS le prix consolider. Formation de BISI/SIBI/BAG = PUISSANT
                      </div>
                      <div className="p-2 bg-background/50 rounded">
                        <strong className="text-cyan-400">Identification 2 :</strong> Bougie opposée directement après la prise de liquidité, SANS consolidation
                      </div>
                      <div className="p-2 bg-green-500/10 rounded border border-green-500/20">
                        <strong className="text-green-400">Interprétation :</strong> Dans la dynamique long terme, il est PEU PROBABLE de revenir toucher ce creux/sommet → haute probabilité de retournement +++
                      </div>
                    </div>
                  </div>

                  <div className="p-3 bg-orange-500/10 rounded-lg border border-orange-500/20">
                    <h5 className="font-semibold text-sm mb-2 text-orange-400">LRLR (Low Resistance Liquidity Run)</h5>
                    <p className="text-xs text-muted-foreground mb-2">
                      <strong>= Zone de prix avec sommets accumulés laissant des SL derrière → résistance FAIBLE</strong>
                    </p>
                    <div className="space-y-2 text-xs">
                      <div className="p-2 bg-background/50 rounded">
                        <strong className="text-orange-400">Identification 1 :</strong> Formation de RQH (Relative Equal Highs) = sommets tous plus bas mais similaires sur lesquels on peut tracer une trendline
                      </div>
                      <div className="p-2 bg-background/50 rounded">
                        <strong className="text-orange-400">Identification 2 :</strong> Formation de creux SANS FVG = aucune puissance. Le prix monte "en escalier"
                      </div>
                      <div className="p-2 bg-yellow-500/10 rounded border border-yellow-500/20">
                        <strong className="text-yellow-400">Interprétation :</strong> Soit la zone est cassée pour une continuation haussière, soit après la prise de liquidité il y a un retournement de tendance
                      </div>
                    </div>
                  </div>

                  <div className="p-2 bg-primary/10 rounded text-xs border border-primary/20">
                    <strong className="text-primary">CONCEPT CLÉ :</strong> Le prix AIME prendre des liquidités AVANT de faire une continuation ou un retournement. Identifiez toujours où se trouve la liquidité !
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* ===== MSS & ORDER BLOCKS ===== */}
              <AccordionItem value="mss" className="border border-red-500/20 rounded-lg px-4">
                <AccordionTrigger className="text-left hover:no-underline">
                  <span className="flex items-center gap-2 font-bold text-red-400">
                    <TrendingDown className="w-5 h-5" />
                    MSS, Order Blocks & Breaker Blocks
                  </span>
                </AccordionTrigger>
                <AccordionContent className="space-y-4 pt-2">
                  {/* MSS */}
                  <div className="p-3 bg-red-500/10 rounded-lg border border-red-500/20">
                    <h5 className="font-semibold text-sm mb-2 text-red-400">MSS (Market Structure Shift)</h5>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <div className="p-2 bg-background/50 rounded text-xs">
                        <strong>M (Double Top)</strong><br/>2 sommets descendants + clôture sous le creux intermédiaire
                      </div>
                      <div className="p-2 bg-background/50 rounded text-xs">
                        <strong>W (Double Bottom)</strong><br/>2 creux ascendants + sommet au-dessus du sommet intermédiaire
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Zones clés de prise de position : confirmation de structure, retest FVG, avalement
                    </p>
                  </div>

                  <div className="p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
                    <h5 className="font-semibold text-sm mb-2 text-yellow-400">COS (Continuation of Structure)</h5>
                    <p className="text-xs text-muted-foreground">
                      = Pas de MSS. Clôture au-dessus du dernier sommet ou en-dessous du dernier creux majeur.<br/>
                      <strong>Probabilité ++ de continuation si COS en HRLR</strong>
                    </p>
                  </div>

                  <div className="p-3 bg-orange-500/10 rounded-lg border border-orange-500/20">
                    <h5 className="font-semibold text-sm mb-2 text-orange-400">Turtle Soup</h5>
                    <p className="text-xs text-muted-foreground">
                      = MSS qui fail<br/>
                      Ex: M top qui se forme juste avant un BSL (piège) → point d'entrée long<br/>
                      <strong>Quand un MSS ne couvre pas un POI majeur, il va souvent fail</strong>
                    </p>
                  </div>

                  {/* Order Block */}
                  <div className="p-3 bg-purple-500/10 rounded-lg border border-purple-500/20">
                    <h5 className="font-semibold text-sm mb-2 text-purple-400">Order Block (OB)</h5>
                    <p className="text-xs text-muted-foreground mb-2">
                      = Point où le marché se fait bloquer. <strong>Bougie de retournement + FVG</strong> immédiatement ou peu après.
                    </p>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="p-2 bg-background/50 rounded">
                        <strong className="text-cyan-400">OB en HRLR</strong><br/>
                        Haute proba retournement +++<br/>
                        Souvent TOP ou BOTTOM
                      </div>
                      <div className="p-2 bg-background/50 rounded">
                        <strong className="text-orange-400">OB en LRLR</strong><br/>
                        Moins de proba<br/>
                        Retest cherche plus profond
                      </div>
                    </div>
                    <div className="mt-2 p-2 bg-background/50 rounded text-xs">
                      <strong>Cas puissant :</strong> OB + prise de liquidité + HRLR + M ou W = combo gagnant
                    </div>
                    <div className="mt-2 p-2 bg-background/50 rounded text-xs">
                      <strong>SL :</strong> Sous la bougie cassée par la reversal ou sous la 1ère bougie de la FVG
                    </div>
                  </div>

                  {/* Breaker Block */}
                  <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
                    <h5 className="font-semibold text-sm mb-2 text-blue-400">Breaker Block (BB)</h5>
                    <p className="text-xs text-muted-foreground mb-2">
                      = <strong>Order Block non respecté</strong> (cassure) qui casse un creux (bearish) ou sommet majeur (bullish)
                    </p>
                    <div className="text-xs text-muted-foreground space-y-1">
                      <p>• Zone : de la 1ère à la 3ème bougie créant la FVG de l'OB</p>
                      <p>• Seuil médian du BB = zone clé</p>
                      <p>• BB en HRLR = plus de proba de reversal/continuation</p>
                      <p>• BB en LRLR = plus de proba de turtle soup</p>
                      <p>• BB + plusieurs FVG = crée un BPR avec la FVG de l'OB → très puissant</p>
                    </div>
                    <div className="mt-2 p-2 bg-red-500/10 rounded text-xs border border-red-500/20">
                      <AlertTriangle className="w-3 h-3 text-red-400 inline mr-1" />
                      <strong>Différent d'un MSS</strong> car il y a une FVG → plus puissant
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* ===== RETRACEMENTS ===== */}
              <AccordionItem value="retracements" className="border border-blue-500/20 rounded-lg px-4">
                <AccordionTrigger className="text-left hover:no-underline">
                  <span className="flex items-center gap-2 font-bold text-blue-400">
                    <Percent className="w-5 h-5" />
                    Les Retracements (Fibonacci)
                  </span>
                </AccordionTrigger>
                <AccordionContent className="space-y-4 pt-2">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="p-3 bg-green-500/10 rounded border border-green-500/20">
                      <div className="font-bold text-green-400 text-lg">38.2%</div>
                      <p className="text-xs text-muted-foreground">
                        <strong>Maintien de tendance</strong><br/>
                        Tant qu'on ne casse pas → continuation<br/>
                        Zone de potentiel rebond et prise de position
                      </p>
                    </div>
                    <div className="p-3 bg-yellow-500/10 rounded border border-yellow-500/20">
                      <div className="font-bold text-yellow-400 text-lg">50%</div>
                      <p className="text-xs text-muted-foreground">
                        Le temps pour arriver ≈ temps pour revenir au départ ou atteindre l'autre extrémité
                      </p>
                    </div>
                    <div className="p-3 bg-orange-500/10 rounded border border-orange-500/20">
                      <div className="font-bold text-orange-400 text-lg">61.8% & 78.6%</div>
                      <p className="text-xs text-muted-foreground">
                        <strong>Zone OTE (Optimal Trade Entry)</strong><br/>
                        Zone de recharge de la tendance<br/>
                        Très pertinent dans dynamiques haussières
                      </p>
                    </div>
                    <div className="p-3 bg-purple-500/10 rounded border border-purple-500/20">
                      <div className="font-bold text-purple-400 text-lg">161.8% & 200%</div>
                      <p className="text-xs text-muted-foreground">
                        <strong>Zone cible</strong> lors d'un avalement<br/>
                        Objectif de Take Profit
                      </p>
                    </div>
                  </div>

                  <div className="p-3 bg-primary/10 rounded-lg border border-primary/20">
                    <h5 className="font-semibold text-sm mb-2 text-primary">Zone de Confluence</h5>
                    <p className="text-xs text-muted-foreground">
                      = Superposition de plusieurs zones de retracement → signal plus fort
                    </p>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* ===== STATE DELIVERY ===== */}
              <AccordionItem value="sd" className="border border-green-500/20 rounded-lg px-4">
                <AccordionTrigger className="text-left hover:no-underline">
                  <span className="flex items-center gap-2 font-bold text-green-400">
                    <Target className="w-5 h-5" />
                    State Delivery (SD)
                  </span>
                </AccordionTrigger>
                <AccordionContent className="space-y-4 pt-2">
                  <p className="text-sm text-muted-foreground">
                    = Le marché se déplace d'un point A à un point B. <strong>Il ne se déplace jamais sans raison</strong> et va chercher des POI.
                  </p>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-green-500/10 rounded border border-green-500/20">
                      <div className="font-semibold text-green-400 text-sm">SD Bullish</div>
                      <p className="text-xs text-muted-foreground mt-1">Va chercher la BSL (Buy-Side Liquidity)</p>
                    </div>
                    <div className="p-3 bg-red-500/10 rounded border border-red-500/20">
                      <div className="font-semibold text-red-400 text-sm">SD Bearish</div>
                      <p className="text-xs text-muted-foreground mt-1">Va chercher la SSL (Sell-Side Liquidity)</p>
                    </div>
                  </div>

                  <div className="p-3 bg-muted/30 rounded-lg">
                    <h5 className="font-semibold text-sm mb-2">Comment identifier le SD ?</h5>
                    <p className="text-xs text-muted-foreground">
                      Le plus précis : <strong>MSS ou OB</strong>, puis les POI pour spéculer sur la livraison du prix<br/>
                      C'est après identification d'un MSS, Breaker Block, IFVG ou Reversal qu'on parie sur une direction
                    </p>
                  </div>

                  <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
                    <h5 className="font-semibold text-sm mb-2 text-blue-400">Multi-Timeframe</h5>
                    <p className="text-xs text-muted-foreground">
                      • La livraison peut être différente selon les UT (bullish en 1M, bearish en 1D)<br/>
                      • Si le SD en grosse UT va dans un sens → intraday probable ++ dans le même sens
                    </p>
                  </div>

                  <div className="p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
                    <h5 className="font-semibold text-sm mb-2 text-yellow-400">Applications Trade :</h5>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      <li>• <strong>Swing</strong> : cherche le SD Daily ou Weekly</li>
                      <li>• <strong>Intraday</strong> : cherche le SD H4 ou H1</li>
                      <li>• <strong>Signal de changement</strong> : prise de liquidité + FVG non respectées → le SD change de sens (encore plus si BPR + bougie vectorielle ou BISI/SIBI)</li>
                    </ul>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* ===== DISPLACEMENT ===== */}
              <AccordionItem value="displacement" className="border border-cyan-500/20 rounded-lg px-4">
                <AccordionTrigger className="text-left hover:no-underline">
                  <span className="flex items-center gap-2 font-bold text-cyan-400">
                    <Zap className="w-5 h-5" />
                    Displacement
                  </span>
                </AccordionTrigger>
                <AccordionContent className="space-y-4 pt-2">
                  <p className="text-sm text-muted-foreground">
                    = <strong>Réaction du prix sur un POI</strong>. On cherche des gros retournements du prix.
                  </p>

                  <div className="p-3 bg-cyan-500/10 rounded-lg border border-cyan-500/20">
                    <h5 className="font-semibold text-sm mb-2 text-cyan-400">Interprétation pour Trade</h5>
                    <p className="text-xs text-muted-foreground">
                      Un <strong>displacement après une prise de liquidité</strong> montre du momentum → signal fort
                    </p>
                    <div className="mt-2 p-2 bg-background/50 rounded text-xs">
                      <strong>Exemple :</strong> Création d'une FVG avec prise de liquidité d'un PDL dans un SD haussier
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

            </Accordion>
          </SectionCard>
        </TabsContent>

        {/* ===== NEWS ÉCO ===== */}
        <TabsContent value="news" className="mt-0">
          <SectionCard icon={Newspaper} title="News Économiques Importantes" color="text-orange-400" bgColor="bg-orange-500/10">
            <div className="space-y-6">
              
              {/* Alerte importante */}
              <div className="p-4 bg-red-500/10 rounded-lg border border-red-500/20">
                <AlertTriangle className="w-5 h-5 text-red-400 inline mr-2" />
                <span className="font-bold text-red-400">IMPORTANT EN INTRADAY</span>
                <p className="text-sm text-muted-foreground mt-2">
                  Toujours identifier les news importantes qui vont entraîner de la volatilité. 
                  Évite d'avoir des positions ouvertes pendant ces événements ou réduis ta taille de position.
                  Tout est visible sur <strong>Forex Factory</strong> - sélectionne uniquement <strong>USD / EUR / JPY</strong>.
                </p>
              </div>

              {/* ===== THÉORIE DE L'INFLATION ===== */}
              <div className="p-4 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-lg border border-blue-500/20">
                <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-blue-400" />
                  Théorie : L'Inflation et les Taux
                </h3>
                
                <p className="text-sm text-muted-foreground mb-4">
                  L'objectif des banques centrales est une <strong className="text-primary">inflation de 2% en moyenne</strong>.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                  <div className="p-3 bg-green-500/10 rounded border border-green-500/20">
                    <div className="font-semibold text-green-400">Inflation modérée (~2%)</div>
                    <p className="text-xs text-muted-foreground mt-1">Économie saine</p>
                  </div>
                  <div className="p-3 bg-red-500/10 rounded border border-red-500/20">
                    <div className="font-semibold text-red-400">Inflation trop forte (&gt;3%)</div>
                    <p className="text-xs text-muted-foreground mt-1">Pouvoir d'achat en danger → hausse des taux</p>
                  </div>
                  <div className="p-3 bg-blue-500/10 rounded border border-blue-500/20">
                    <div className="font-semibold text-blue-400">Déflation (&lt;0%)</div>
                    <p className="text-xs text-muted-foreground mt-1">Économie bloquée → baisse des taux</p>
                  </div>
                </div>

                <div className="p-3 bg-muted/30 rounded-lg">
                  <h4 className="font-semibold text-sm mb-2">Conséquences sur les marchés :</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div className="p-2 bg-red-500/10 rounded border border-red-500/20">
                      <div className="font-semibold text-red-400">Taux ↑</div>
                      <p className="text-xs text-muted-foreground">
                        Crédits chers → Entreprises gagnent moins → <strong>Actions ↓</strong>
                      </p>
                    </div>
                    <div className="p-2 bg-green-500/10 rounded border border-green-500/20">
                      <div className="font-semibold text-green-400">Taux ↓</div>
                      <p className="text-xs text-muted-foreground">
                        Argent facile → Investissements → <strong>Actions ↑</strong>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* ===== NFP ===== */}
              <Accordion type="multiple" className="w-full space-y-2">
                <AccordionItem value="nfp" className="border border-red-500/20 rounded-lg px-4">
                  <AccordionTrigger className="text-left hover:no-underline">
                    <span className="flex items-center gap-2">
                      <Badge variant="destructive">Impact Fort</Badge>
                      <span className="font-bold">NFP (Non-Farm Payrolls)</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="space-y-4 pt-2">
                    <p className="text-sm text-muted-foreground">
                      <strong>Nombre d'emplois créés (ou détruits) hors secteur agricole aux États-Unis.</strong> 
                      C'est l'indicateur clé du marché du travail : il influence fortement le dollar, les marchés actions et les taux.
                    </p>
                    <div className="p-2 bg-primary/10 rounded text-sm">
                      👉 Le NFP dit si beaucoup de gens trouvent un travail ou non.
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/20">
                        <h5 className="font-semibold text-green-400 mb-2">NFP en HAUSSE (beaucoup d'emplois)</h5>
                        <ul className="text-xs text-muted-foreground space-y-1">
                          <li>• Les gens gagnent de l'argent</li>
                          <li>• Ils consomment plus</li>
                          <li>• Les prix risquent de monter (inflation)</li>
                        </ul>
                        <div className="mt-2 p-2 bg-background/50 rounded text-xs">
                          <strong className="text-red-400">Donc :</strong><br/>
                          • Banque centrale peut <strong>augmenter les taux</strong><br/>
                          • <strong>Actions ↓</strong><br/>
                          • <strong>Dollar ↑</strong>
                        </div>
                      </div>
                      <div className="p-3 bg-red-500/10 rounded-lg border border-red-500/20">
                        <h5 className="font-semibold text-red-400 mb-2">NFP en BAISSE (peu d'emplois)</h5>
                        <ul className="text-xs text-muted-foreground space-y-1">
                          <li>• L'économie ralentit</li>
                          <li>• Moins de consommation</li>
                          <li>• Moins de pression sur les prix</li>
                        </ul>
                        <div className="mt-2 p-2 bg-background/50 rounded text-xs">
                          <strong className="text-green-400">Donc :</strong><br/>
                          • Banque centrale peut <strong>baisser les taux</strong><br/>
                          • <strong>Actions ↑</strong><br/>
                          • <strong>Dollar ↓</strong>
                        </div>
                      </div>
                    </div>

                    <div className="p-2 bg-orange-500/10 rounded text-xs border border-orange-500/20">
                      <Clock className="w-3 h-3 text-orange-400 inline mr-1" />
                      Publié le <strong>1er vendredi de chaque mois</strong> à <strong>14h30 (Paris)</strong>. 
                      Peut faire bouger le marché de 1-3% en quelques minutes.
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* ===== CPI ===== */}
                <AccordionItem value="cpi" className="border border-orange-500/20 rounded-lg px-4">
                  <AccordionTrigger className="text-left hover:no-underline">
                    <span className="flex items-center gap-2">
                      <Badge variant="destructive">Impact Fort</Badge>
                      <span className="font-bold">CPI (Consumer Price Index)</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="space-y-4 pt-2">
                    <p className="text-sm text-muted-foreground">
                      <strong>Indice des prix à la consommation.</strong> Il mesure l'inflation côté consommateurs 
                      (ce que paient les ménages pour les biens et services). <strong>C'est ce qui fait le plus bouger les marchés.</strong>
                    </p>
                    <div className="p-2 bg-primary/10 rounded text-sm">
                      👉 Le CPI mesure si la vie devient plus chère (courses, loyer, énergie).
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-3 bg-red-500/10 rounded-lg border border-red-500/20">
                        <h5 className="font-semibold text-red-400 mb-2">CPI en HAUSSE (vie plus chère)</h5>
                        <ul className="text-xs text-muted-foreground space-y-1">
                          <li>• Inflation élevée = problème</li>
                          <li>• La banque centrale veut la freiner</li>
                        </ul>
                        <div className="mt-2 p-2 bg-background/50 rounded text-xs">
                          <strong className="text-red-400">Donc :</strong><br/>
                          • <strong>Taux ↑</strong><br/>
                          • <strong>Actions ↓</strong><br/>
                          • <strong>Dollar ↑</strong>
                        </div>
                      </div>
                      <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/20">
                        <h5 className="font-semibold text-green-400 mb-2">CPI en BAISSE (prix qui ralentissent)</h5>
                        <ul className="text-xs text-muted-foreground space-y-1">
                          <li>• Inflation sous contrôle</li>
                          <li>• Moins besoin de taux élevés</li>
                        </ul>
                        <div className="mt-2 p-2 bg-background/50 rounded text-xs">
                          <strong className="text-green-400">Donc :</strong><br/>
                          • <strong>Taux ↓</strong><br/>
                          • <strong>Actions ↑</strong><br/>
                          • <strong>Dollar ↓</strong>
                        </div>
                      </div>
                    </div>

                    <div className="p-2 bg-orange-500/10 rounded text-xs border border-orange-500/20">
                      <Clock className="w-3 h-3 text-orange-400 inline mr-1" />
                      Publié <strong>mensuellement autour du 10-15</strong> à <strong>14h30 (Paris)</strong>.
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* ===== PPI ===== */}
                <AccordionItem value="ppi" className="border border-yellow-500/20 rounded-lg px-4">
                  <AccordionTrigger className="text-left hover:no-underline">
                    <span className="flex items-center gap-2">
                      <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">Impact Moyen</Badge>
                      <span className="font-bold">PPI (Producer Price Index)</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="space-y-4 pt-2">
                    <p className="text-sm text-muted-foreground">
                      <strong>Indice des prix à la production.</strong> Il mesure l'inflation en amont, du point de vue des entreprises. 
                      Souvent vu comme un <strong>signal avancé du CPI</strong>.
                    </p>
                    <div className="p-2 bg-primary/10 rounded text-sm">
                      👉 Le PPI regarde si les entreprises paient plus cher pour produire.
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
                        <h5 className="font-semibold text-yellow-400 mb-2">PPI en HAUSSE</h5>
                        <ul className="text-xs text-muted-foreground space-y-1">
                          <li>• Les entreprises ont plus de coûts</li>
                          <li>• Elles risquent d'augmenter leurs prix plus tard</li>
                        </ul>
                        <div className="mt-2 p-2 bg-background/50 rounded text-xs">
                          <strong className="text-yellow-400">⚠️ Donc :</strong><br/>
                          • Inflation possible à venir<br/>
                          • Réaction marché <strong>modérée</strong>
                        </div>
                      </div>
                      <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/20">
                        <h5 className="font-semibold text-green-400 mb-2">PPI en BAISSE</h5>
                        <ul className="text-xs text-muted-foreground space-y-1">
                          <li>• Coûts des entreprises en baisse</li>
                          <li>• Moins de pression sur les prix</li>
                        </ul>
                        <div className="mt-2 p-2 bg-background/50 rounded text-xs">
                          <strong className="text-green-400">Donc :</strong><br/>
                          • Plutôt <strong>positif pour les actions</strong><br/>
                          • Impact plus faible que CPI
                        </div>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* ===== PCE ===== */}
                <AccordionItem value="pce" className="border border-blue-500/20 rounded-lg px-4">
                  <AccordionTrigger className="text-left hover:no-underline">
                    <span className="flex items-center gap-2">
                      <Badge variant="destructive">Impact Fort</Badge>
                      <span className="font-bold">PCE (Personal Consumption Expenditures)</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="space-y-4 pt-2">
                    <p className="text-sm text-muted-foreground">
                      <strong>Indice des dépenses de consommation personnelle.</strong> C'est l'indicateur d'inflation 
                      <strong className="text-blue-400"> préféré de la Fed</strong>, car il est plus large et plus flexible que le CPI.
                    </p>
                    <div className="p-2 bg-primary/10 rounded text-sm">
                      👉 Le PCE est la mesure d'inflation que la Fed regarde en priorité pour décider des taux.
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-3 bg-red-500/10 rounded-lg border border-red-500/20">
                        <h5 className="font-semibold text-red-400 mb-2">PCE en HAUSSE</h5>
                        <ul className="text-xs text-muted-foreground space-y-1">
                          <li>• Inflation confirmée</li>
                          <li>• La banque centrale devient stricte</li>
                        </ul>
                        <div className="mt-2 p-2 bg-background/50 rounded text-xs">
                          <strong className="text-red-400">Donc :</strong><br/>
                          • Taux élevés <strong>plus longtemps</strong><br/>
                          • <strong>Actions ↓↓</strong> (fort)<br/>
                          • <strong>Dollar ↑↑</strong> (fort)
                        </div>
                      </div>
                      <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/20">
                        <h5 className="font-semibold text-green-400 mb-2">PCE en BAISSE</h5>
                        <ul className="text-xs text-muted-foreground space-y-1">
                          <li>• Inflation vraiment en recul</li>
                          <li>• La banque centrale peut assouplir</li>
                        </ul>
                        <div className="mt-2 p-2 bg-background/50 rounded text-xs">
                          <strong className="text-green-400">Donc :</strong><br/>
                          • <strong>Actions ↑↑</strong> (fort)<br/>
                          • <strong>Dollar ↓</strong>
                        </div>
                      </div>
                    </div>

                    <div className="p-2 bg-blue-500/10 rounded text-xs border border-blue-500/20">
                      <Clock className="w-3 h-3 text-blue-400 inline mr-1" />
                      Publié <strong>fin de mois</strong> à <strong>14h30 (Paris)</strong>.
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* ===== FOMC ===== */}
                <AccordionItem value="fomc" className="border border-purple-500/20 rounded-lg px-4">
                  <AccordionTrigger className="text-left hover:no-underline">
                    <span className="flex items-center gap-2">
                      <Badge variant="destructive">Impact Fort</Badge>
                      <span className="font-bold">FOMC (Federal Open Market Committee)</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="space-y-4 pt-2">
                    <p className="text-sm text-muted-foreground">
                      <strong>Comité de la Réserve fédérale américaine</strong> qui décide de la politique monétaire :
                      taux d'intérêt, rythme des hausses/baisses de taux, discours et projections économiques.
                    </p>
                    <div className="p-2 bg-primary/10 rounded text-sm">
                      👉 Le FOMC, c'est quand la banque centrale parle et décide.
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-3 bg-red-500/10 rounded-lg border border-red-500/20">
                        <h5 className="font-semibold text-red-400 mb-2 flex items-center gap-2">
                          🦅 HAWKISH (Discours dur)
                        </h5>
                        <p className="text-xs text-muted-foreground mb-2">
                          La Fed est préoccupée par l'inflation et veut être stricte.
                        </p>
                        <div className="p-2 bg-background/50 rounded text-xs">
                          • <strong>USD ↑</strong><br/>
                          • <strong>Indices ↓</strong><br/>
                          • <strong>Crypto ↓</strong>
                        </div>
                      </div>
                      <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/20">
                        <h5 className="font-semibold text-green-400 mb-2 flex items-center gap-2">
                          🕊️ DOVISH (Discours souple)
                        </h5>
                        <p className="text-xs text-muted-foreground mb-2">
                          La Fed est confiante et peut assouplir sa politique.
                        </p>
                        <div className="p-2 bg-background/50 rounded text-xs">
                          • <strong>USD ↓</strong><br/>
                          • <strong>Indices ↑</strong><br/>
                          • <strong>Crypto ↑</strong>
                        </div>
                      </div>
                    </div>

                    <div className="p-2 bg-purple-500/10 rounded text-xs border border-purple-500/20">
                      <Clock className="w-3 h-3 text-purple-400 inline mr-1" />
                      <strong>8 réunions par an</strong>. Statement à <strong>20h00 (Paris)</strong>, 
                      Press Conference à <strong>20h30</strong>. C'est l'événement le plus important !
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>

              {/* Résumé visuel */}
              <div className="p-4 bg-muted/30 rounded-lg">
                <h4 className="font-semibold mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-primary" />
                  Résumé : Impact sur les marchés
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="py-2 px-3 text-left">Indicateur</th>
                        <th className="py-2 px-3 text-center">Si ↑</th>
                        <th className="py-2 px-3 text-center">Si ↓</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-border/30">
                        <td className="py-2 px-3 font-semibold text-red-400">NFP</td>
                        <td className="py-2 px-3 text-center">USD↑ Actions↓</td>
                        <td className="py-2 px-3 text-center">USD↓ Actions↑</td>
                      </tr>
                      <tr className="border-b border-border/30">
                        <td className="py-2 px-3 font-semibold text-orange-400">CPI</td>
                        <td className="py-2 px-3 text-center">USD↑ Actions↓</td>
                        <td className="py-2 px-3 text-center">USD↓ Actions↑</td>
                      </tr>
                      <tr className="border-b border-border/30">
                        <td className="py-2 px-3 font-semibold text-yellow-400">PPI</td>
                        <td className="py-2 px-3 text-center">Impact modéré</td>
                        <td className="py-2 px-3 text-center">Actions↑</td>
                      </tr>
                      <tr className="border-b border-border/30">
                        <td className="py-2 px-3 font-semibold text-blue-400">PCE</td>
                        <td className="py-2 px-3 text-center">USD↑↑ Actions↓↓</td>
                        <td className="py-2 px-3 text-center">USD↓ Actions↑↑</td>
                      </tr>
                      <tr>
                        <td className="py-2 px-3 font-semibold text-purple-400">FOMC</td>
                        <td className="py-2 px-3 text-center">🦅 Hawkish → USD↑ Indices↓</td>
                        <td className="py-2 px-3 text-center">🕊️ Dovish → USD↓ Indices↑</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Lien Forex Factory */}
              <div className="p-4 bg-primary/10 rounded-lg border border-primary/20">
                <Info className="w-4 h-4 text-primary inline mr-2" />
                <span className="text-sm">
                  <strong>Conseil :</strong> Consulte <strong>Forex Factory</strong> avant chaque session. 
                  Filtre sur <strong>USD / EUR / JPY</strong> et ne garde que les événements à <strong>impact fort</strong> (rouge).
                </span>
              </div>
            </div>
          </SectionCard>
        </TabsContent>

        {/* ===== PRATIQUE ===== */}
        <TabsContent value="practice" className="mt-0">
          {/* Disclaimer Banner - Avertissement légal */}
          <DisclaimerBanner language={language} className="mb-6" />

          <SectionCard icon={Target} title="Pratique : Créer un Trade" color="text-green-400" bgColor="bg-green-500/10">
            <div className="space-y-6">
              <p className="text-muted-foreground">
                Voici un exemple concret de création de trade en respectant toutes les règles de gestion du risque.
              </p>

              {/* Étape par étape */}
              <div className="space-y-4">
                <div className="p-4 bg-muted/30 rounded-lg border-l-4 border-blue-500">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-6 h-6 rounded-full bg-blue-500 text-white text-sm flex items-center justify-center">1</span>
                    <h4 className="font-semibold">Définir ton Capital et Risque</h4>
                  </div>
                  <div className="text-sm text-muted-foreground space-y-1">
                    <p>Capital total : <strong>52 000$</strong></p>
                    <p>Risque par trade : <strong>1%</strong></p>
                    <p>→ Perte max autorisée : <strong>520$</strong></p>
                  </div>
                </div>

                <div className="p-4 bg-muted/30 rounded-lg border-l-4 border-purple-500">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-6 h-6 rounded-full bg-purple-500 text-white text-sm flex items-center justify-center">2</span>
                    <h4 className="font-semibold">Analyser le Graphique</h4>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    <p>Actif : <strong>ETH/USDT</strong></p>
                    <p>Prix actuel : <strong>4 300$</strong></p>
                    <p>Analyse : Tendance haussière, RSI à 45, MACD bullish cross imminent</p>
                    <p>Direction : <strong className="text-green-400">LONG</strong></p>
                  </div>
                </div>

                <div className="p-4 bg-muted/30 rounded-lg border-l-4 border-red-500">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-6 h-6 rounded-full bg-red-500 text-white text-sm flex items-center justify-center">3</span>
                    <h4 className="font-semibold">Définir les Niveaux</h4>
                  </div>
                  <div className="text-sm text-muted-foreground space-y-1">
                    <p>Entry (Entrée) : <strong>4 300$</strong></p>
                    <p>Stop-Loss : <strong>4 100$</strong> (support identifié)</p>
                    <p>Take-Profit 1 : <strong>4 500$</strong> (R:R 1:1)</p>
                    <p>Take-Profit 2 : <strong>4 700$</strong> (R:R 1:2)</p>
                    <p>Take-Profit 100% : <strong>4 900$</strong> (R:R 1:3)</p>
                  </div>
                </div>

                <div className="p-4 bg-muted/30 rounded-lg border-l-4 border-green-500">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-6 h-6 rounded-full bg-green-500 text-white text-sm flex items-center justify-center">4</span>
                    <h4 className="font-semibold">Calculer la Taille de Position</h4>
                  </div>
                  <div className="text-sm text-muted-foreground space-y-1">
                    <p>Risque par unité : 4 300$ - 4 100$ = <strong>200$/ETH</strong></p>
                    <p>Taille position : 520$ / 200$ = <strong>2,6 ETH</strong></p>
                    <p>Valeur position : 2,6 × 4 300$ = <strong>11 180$</strong></p>
                  </div>
                </div>

                <div className="p-4 bg-muted/30 rounded-lg border-l-4 border-yellow-500">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-6 h-6 rounded-full bg-yellow-500 text-white text-sm flex items-center justify-center">5</span>
                    <h4 className="font-semibold">Vérifier le Ratio Risque/Gain</h4>
                  </div>
                  <div className="text-sm text-muted-foreground space-y-1">
                    <p>Perte potentielle : 520$ (au SL)</p>
                    <p>Gain potentiel TP2 : 400$ × 2,6 = <strong>1 040$</strong></p>
                    <p>R:R = 1 040$ / 520$ = <strong className="text-green-400">1:2 ✓</strong></p>
                  </div>
                </div>
              </div>

              {/* Résumé Final */}
              <div className="p-4 bg-gradient-to-r from-primary/20 to-green-500/20 rounded-lg border border-primary/30">
                <h4 className="font-bold text-lg mb-3 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  Récapitulatif du Trade
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Actif</span>
                    <div className="font-bold">ETH/USDT</div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Direction</span>
                    <div className="font-bold text-green-400">LONG</div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Taille</span>
                    <div className="font-bold">2,6 ETH</div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">R:R</span>
                    <div className="font-bold text-green-400">1:2.1</div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Entry</span>
                    <div className="font-bold">4 300$</div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Stop-Loss</span>
                    <div className="font-bold text-red-400">4 100$</div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">TP1</span>
                    <div className="font-bold">4 500$</div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">TP2</span>
                    <div className="font-bold text-green-400">4 700$</div>
                  </div>
                </div>
              </div>

              {/* ===== FRAIS BROKER — Explication pédagogique ===== */}
              <div className="mt-8">
                <div className="p-4 bg-amber-500/10 rounded-lg border border-amber-500/20 mb-4">
                  <AlertTriangle className="w-5 h-5 text-amber-400 inline mr-2" />
                  <span className="font-bold text-amber-400">FRAIS BROKER — Pourquoi ton PnL réel diffère de celui affiché</span>
                  <p className="text-sm text-muted-foreground mt-2">
                    Tous les gains et pertes calculés par TRADEX AI sont <strong>bruts de frais</strong>. Ton broker prélève des frais à plusieurs endroits qui réduisent ton PnL réel. Voici les 4 catégories que tu dois connaître.
                  </p>
                </div>

                <div className="space-y-4">
                  {/* 1. Frais universels */}
                  <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="w-6 h-6 rounded-full bg-primary text-white text-sm flex items-center justify-center font-bold">1</span>
                      <h4 className="font-bold text-primary">Frais universels (tous brokers, tous actifs)</h4>
                    </div>
                    <ul className="text-sm text-muted-foreground space-y-1.5 list-disc pl-5">
                      <li><strong className="text-foreground">Spread bid/ask</strong> : différence entre prix d'achat et de vente. Payé immédiatement à l'entrée. Ordre de grandeur : <strong>0,01 % à 0,3 %</strong> selon l'actif.</li>
                      <li><strong className="text-foreground">Commission par ordre</strong> : montant fixe ou pourcentage à chaque exécution. À l'entrée <em>ET</em> à la sortie. <strong>0 % à 0,25 %</strong> par ordre.</li>
                      <li><strong className="text-foreground">Slippage</strong> : écart entre le prix demandé et le prix réellement exécuté quand le marché va vite. Jusqu'à <strong>2 % sur une news</strong>.</li>
                      <li><strong className="text-foreground">Conversion devise</strong> : si ton compte est en € et l'actif est en $, la conversion coûte <strong>0,25 % à 1 %</strong>.</li>
                    </ul>
                  </div>

                  {/* 2. Crypto perpétuel */}
                  <div className="p-4 bg-amber-500/5 rounded-lg border border-amber-500/20">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="w-6 h-6 rounded-full bg-amber-500 text-white text-sm flex items-center justify-center font-bold">2</span>
                      <h4 className="font-bold text-amber-400">Crypto perpétuel (Binance, Bybit, Bitget, MEXC)</h4>
                      <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-xs">Le plus impactant</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">
                      <strong className="text-foreground">Funding rate</strong> : prélevé <strong>toutes les 8 heures</strong> (00 h, 08 h, 16 h UTC). Formule :
                      <code className="bg-background/50 px-1.5 py-0.5 rounded mx-1 text-xs font-mono">Notional × Funding Rate</code>.
                    </p>
                    <p className="text-sm text-muted-foreground mb-3">
                      Si tu es <strong>LONG et funding rate positif</strong>, tu paies aux shorts. En marché bullish tendu, le funding peut atteindre <strong className="text-amber-400">+0,3 % / 8 h</strong> — soit <strong>−0,9 % par jour</strong> sur ton notionnel.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                      <div className="p-2 bg-background/50 rounded">
                        <strong className="text-foreground">Binance USDT-M</strong> — maker 0,020 % / taker 0,050 % <span className="text-amber-400">+ funding</span>
                      </div>
                      <div className="p-2 bg-background/50 rounded">
                        <strong className="text-foreground">Bybit</strong> — maker 0,020 % / taker 0,055 % <span className="text-amber-400">+ funding</span>
                      </div>
                      <div className="p-2 bg-background/50 rounded">
                        <strong className="text-foreground">Bitget</strong> — maker 0,020 % / taker 0,060 % <span className="text-amber-400">+ funding</span>
                      </div>
                      <div className="p-2 bg-background/50 rounded">
                        <strong className="text-foreground">MEXC</strong> — maker 0,000 % / taker 0,020 % <span className="text-amber-400">+ funding</span>
                      </div>
                    </div>
                    <div className="mt-3 p-2.5 bg-amber-500/10 rounded text-xs border border-amber-500/20">
                      <strong className="text-amber-400">Exemple concret :</strong> LONG BTC 10 000 $ × 10 (notionnel 100 000 $) pendant 3 jours à funding moyen 0,03 % / 8 h = 9 × 0,03 % = 0,27 % du notionnel = <strong>−27 $</strong> de frais funding en plus des commissions.
                    </div>
                  </div>

                  {/* 3. Crypto spot */}
                  <div className="p-4 bg-green-500/5 rounded-lg border border-green-500/20">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="w-6 h-6 rounded-full bg-green-500 text-white text-sm flex items-center justify-center font-bold">3</span>
                      <h4 className="font-bold text-green-400">Crypto spot (sans levier)</h4>
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">
                      Pas de funding rate (pas de levier natif), mais des <strong>commissions plus élevées</strong> qu'en perpétuel.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                      <div className="p-2 bg-background/50 rounded">
                        <strong className="text-foreground">Binance</strong> — 0,10 % maker/taker (0,075 % en payant en BNB)
                      </div>
                      <div className="p-2 bg-background/50 rounded">
                        <strong className="text-foreground">Coinbase Advanced</strong> — 0 – 0,40 % maker / 0,05 – 0,60 % taker
                      </div>
                      <div className="p-2 bg-background/50 rounded">
                        <strong className="text-foreground">Kraken</strong> — 0,25 % maker / 0,40 % taker
                      </div>
                    </div>
                  </div>

                  {/* 4. CFD */}
                  <div className="p-4 bg-red-500/5 rounded-lg border border-red-500/20">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="w-6 h-6 rounded-full bg-red-500 text-white text-sm flex items-center justify-center font-bold">4</span>
                      <h4 className="font-bold text-red-400">CFD (actions, indices, matières premières)</h4>
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">
                      <strong className="text-foreground">Overnight financing</strong> : intérêts calculés sur le <strong>notionnel</strong> (pas la marge). Formule :
                      <code className="bg-background/50 px-1.5 py-0.5 rounded mx-1 text-xs font-mono">Notional × (Taux ± Markup) / 365</code>.
                    </p>
                    <p className="text-sm text-muted-foreground mb-3">
                      ⚠️ Charge <strong className="text-red-400">triple le mercredi soir</strong> (compense les 3 jours calendaires du week-end).
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                      <div className="p-2 bg-background/50 rounded">
                        <strong className="text-foreground">IG</strong> — +1 % markup Forex, <strong className="text-red-400">+3 % markup</strong> indices/actions/matières
                      </div>
                      <div className="p-2 bg-background/50 rounded">
                        <strong className="text-foreground">XTB</strong> — ~2,5 % markup annualisé
                      </div>
                      <div className="p-2 bg-background/50 rounded">
                        <strong className="text-foreground">Trading 212</strong> — 3 – 5 % markup (haut du panier)
                      </div>
                      <div className="p-2 bg-background/50 rounded">
                        <strong className="text-foreground">eToro</strong> — ~5 – 6 % markup + admin fee après 7 jours
                      </div>
                    </div>
                    <div className="mt-3 p-2.5 bg-red-500/10 rounded text-xs border border-red-500/20">
                      <strong className="text-red-400">Exemple concret :</strong> LONG SPX 10 000 € × 10 chez IG pendant 30 jours = notionnel 100 000 €. Coût overnight : ~5 % / 365 × 30 = <strong>~410 €</strong>. Invisible dans le PnL brut affiché par TRADEX AI.
                    </div>
                  </div>

                  {/* Recommandation d'utilisation */}
                  <div className="p-4 bg-primary/10 rounded-lg border border-primary/30">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-5 h-5 text-primary" />
                      <h4 className="font-bold text-primary">Comment renseigner ton champ « Frais estimés »</h4>
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">
                      Dans le <strong>Calculateur</strong>, le <strong>Journal</strong>, le <strong>Track Record</strong> et l'<strong>Analyse IA</strong>, un champ « Frais estimés » te permet d'entrer le total <strong>aller-retour</strong> que ton broker prélève typiquement. Il sera déduit automatiquement du gain affiché.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                      <div className="p-2 bg-background/50 rounded">
                        <strong className="text-primary">Crypto perp scalp</strong> (&lt; 1 h) : ~<strong>0,15 %</strong>
                      </div>
                      <div className="p-2 bg-background/50 rounded">
                        <strong className="text-primary">Crypto perp swing</strong> (2-3 j) : ~<strong>0,30 %</strong>
                      </div>
                      <div className="p-2 bg-background/50 rounded">
                        <strong className="text-primary">CFD indices intraday</strong> : ~<strong>0,50 %</strong>
                      </div>
                      <div className="p-2 bg-background/50 rounded">
                        <strong className="text-primary">CFD à levier sur 30 j</strong> : ~<strong>1,5 %</strong> ou plus
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ===== HORAIRES DE TRADING ===== */}
              <div className="mt-8">
                <div className="p-4 bg-red-500/10 rounded-lg border border-red-500/20 mb-4">
                  <AlertTriangle className="w-5 h-5 text-red-400 inline mr-2" />
                  <span className="font-bold text-red-400">IMPORTANT EN INTRADAY - Horaires de Trading</span>
                  <p className="text-sm text-muted-foreground mt-2">
                    Il existe des heures dans la session où on retrouve plus de volatilité. <strong>Toutes les heures sont en heure de New York (ET)</strong>.
                  </p>
                </div>

                <div className="space-y-4">
                  {/* London Kill Zone */}
                  <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/20">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-bold text-blue-400 flex items-center gap-2">
                        <Clock className="w-5 h-5" />
                        London Kill Zone
                      </h4>
                      <div className="flex gap-2 flex-wrap">
                        <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">3H - 6H <span className="text-blue-300/70">(9H - 12H Paris)</span></Badge>
                        <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">8H - 11H <span className="text-blue-300/70">(14H - 17H Paris)</span></Badge>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      C'est souvent la zone de <strong>bottom</strong> ou de la prise <strong>BSL/SSL</strong> pour donner la direction lors de la session américaine.
                    </p>
                    <div className="p-2 bg-background/50 rounded text-xs">
                      <strong className="text-blue-400">Interprétation :</strong> Haussier avec prise de SSL sur la session d'après → probabilité ++ de continuation.
                    </div>
                  </div>

                  {/* NY AM - Session la plus importante */}
                  <div className="p-4 bg-green-500/10 rounded-lg border border-green-500/20">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-bold text-green-400 flex items-center gap-2">
                        <Clock className="w-5 h-5" />
                        NY AM (Session la plus importante)
                      </h4>
                      <Badge className="bg-green-500/20 text-green-400 border-green-500/30">9H30 - 12H <span className="text-green-300/70">(15H30 - 18H Paris)</span></Badge>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
                      <div className="p-2 bg-background/50 rounded text-xs">
                        <strong className="text-yellow-400">Kill Zone 1 :</strong> 8H30 - 10H <span className="text-yellow-300/70">(14H30 - 16H Paris)</span>
                      </div>
                      <div className="p-2 bg-background/50 rounded text-xs">
                        <strong className="text-yellow-400">Kill Zone 2 :</strong> 10H - 12H <span className="text-yellow-300/70">(16H - 18H Paris)</span>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      <strong>Reversal ou continuation</strong> de la session London. À mettre en relation avec les signes et mouvements de prix.
                    </p>
                    <div className="p-2 bg-yellow-500/10 rounded text-xs border border-yellow-500/20 mb-2">
                      <AlertTriangle className="w-3 h-3 text-yellow-400 inline mr-1" />
                      <strong className="text-yellow-400">Kill Zone :</strong> Beaucoup de volatilité car beaucoup de trades sont lancés. <strong>C'est souvent une manipulation !</strong>
                    </div>
                    <div className="p-2 bg-background/50 rounded text-xs">
                      <strong className="text-green-400">Interprétation :</strong> En SD baissier, dans une FVG daily, si on a un gros rush haussier → probable piège pour repartir à la baisse. Trade en short sur retest de cette FVG lors d'un breaker ou MSS.
                    </div>
                  </div>

                  {/* NY PM */}
                  <div className="p-4 bg-orange-500/10 rounded-lg border border-orange-500/20">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-bold text-orange-400 flex items-center gap-2">
                        <Clock className="w-5 h-5" />
                        NY PM
                      </h4>
                      <div className="flex gap-2 flex-wrap">
                        <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30">13H30 - 16H <span className="text-orange-300/70">(19H30 - 22H Paris)</span></Badge>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      <strong>Reversal ou continuation</strong> de la session du matin. Souvent les POI sont atteints dans ces horaires (<strong>TOP ou BOTTOM</strong>).
                    </p>
                    <div className="p-2 bg-background/50 rounded text-xs">
                      <strong className="text-orange-400">Interprétation :</strong> C'est dans cette zone que si on prend les BSL ou SSL, on prend nos <strong>Take Profits</strong>.
                    </div>
                  </div>

                  {/* Asia */}
                  <div className="p-4 bg-purple-500/10 rounded-lg border border-purple-500/20">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-bold text-purple-400 flex items-center gap-2">
                        <Clock className="w-5 h-5" />
                        Asia
                      </h4>
                      <div className="flex gap-2 flex-wrap">
                        <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">20H - 00H <span className="text-purple-300/70">(02H - 06H Paris)</span></Badge>
                        <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">00H - 5H <span className="text-purple-300/70">(06H - 11H Paris)</span></Badge>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      <strong>Très souvent de la consolidation</strong>, pas beaucoup de mouvement. Éviter de trader pendant cette session sauf si setup très clair.
                    </p>
                  </div>
                </div>

                {/* Résumé visuel des sessions */}
                <div className="mt-4 p-4 bg-muted/30 rounded-lg">
                  <h5 className="font-semibold mb-3 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-primary" />
                    Résumé des Sessions (Heure NY → Paris)
                  </h5>
                  <div className="overflow-x-auto">
                    <div className="flex gap-1 min-w-max">
                      <div className="flex-1 min-w-[100px] p-2 bg-purple-500/20 rounded text-center text-xs">
                        <div className="font-bold text-purple-400">ASIA</div>
                        <div className="text-muted-foreground">00-05H NY</div>
                        <div className="text-purple-300/70 text-[10px]">(06-11H Paris)</div>
                        <div className="text-purple-400 text-[10px] mt-1">Consolidation</div>
                      </div>
                      <div className="flex-1 min-w-[100px] p-2 bg-blue-500/20 rounded text-center text-xs">
                        <div className="font-bold text-blue-400">LONDON</div>
                        <div className="text-muted-foreground">03-11H NY</div>
                        <div className="text-blue-300/70 text-[10px]">(09-17H Paris)</div>
                        <div className="text-blue-400 text-[10px] mt-1">Setup Direction</div>
                      </div>
                      <div className="flex-1 min-w-[110px] p-2 bg-green-500/30 rounded text-center text-xs border-2 border-green-500/50">
                        <div className="font-bold text-green-400">NY AM</div>
                        <div className="text-muted-foreground">09H30-12H NY</div>
                        <div className="text-green-300/70 text-[10px]">(15H30-18H Paris)</div>
                        <div className="text-green-400 text-[10px] mt-1">PLUS IMPORTANT</div>
                      </div>
                      <div className="flex-1 min-w-[100px] p-2 bg-orange-500/20 rounded text-center text-xs">
                        <div className="font-bold text-orange-400">NY PM</div>
                        <div className="text-muted-foreground">13H30-16H NY</div>
                        <div className="text-orange-300/70 text-[10px]">(19H30-22H Paris)</div>
                        <div className="text-orange-400 text-[10px] mt-1">TP / Reversal</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ===== GESTION D'UN TRADE ===== */}
              <div className="mt-8">
                <div className="p-4 bg-gradient-to-r from-primary/20 to-blue-500/20 rounded-lg border border-primary/30 mb-6">
                  <h3 className="font-bold text-xl mb-2 flex items-center gap-2">
                    <Target className="w-6 h-6 text-primary" />
                    Gestion d'un Trade
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Comment gérer activement ton Stop-Loss et Take-Profit pendant un trade pour maximiser tes gains et protéger ton capital.
                  </p>
                </div>

                {/* Section Stop-Loss */}
                <div className="space-y-4 mb-8">
                  <h4 className="font-bold text-lg flex items-center gap-2 text-red-400">
                    <Shield className="w-5 h-5" />
                    1. Gestion du Stop-Loss (SL)
                  </h4>
                  
                  <div className="p-4 bg-green-500/10 rounded-lg border border-green-500/20">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-5 h-5 text-green-400" />
                      <span className="font-semibold text-green-400">Oui, tu peux déplacer ton SL !</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Sur toutes les plateformes (Binance, Bybit, Kraken...), tu peux modifier ton SL à tout moment :
                    </p>
                    <ul className="text-sm text-muted-foreground mt-2 space-y-1 ml-4">
                      <li>• Le <strong>rapprocher</strong> → réduire ton risque</li>
                      <li>• Le <strong>remonter</strong> une fois en gain → sécuriser tes profits</li>
                    </ul>
                  </div>

                  {/* Les 3 usages du SL */}
                  <div className="space-y-3">
                    <h5 className="font-semibold text-sm text-muted-foreground">Les 3 usages principaux du déplacement du SL :</h5>
                    
                    {/* A) Break-even */}
                    <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/20">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="w-6 h-6 rounded-full bg-blue-500 text-white text-sm flex items-center justify-center font-bold">A</span>
                        <span className="font-semibold text-blue-400">Réduire le risque (Break-even stop)</span>
                      </div>
                      <div className="text-sm text-muted-foreground space-y-2">
                        <p><strong>Exemple :</strong> Tu entres sur BTC à 113k, SL = 111k.</p>
                        <p>Le prix monte à 115k → Tu remontes ton SL à <strong>113k</strong> (ton prix d'entrée).</p>
                        <div className="p-2 bg-background/50 rounded mt-2">
                          <span className="text-green-400 font-semibold">Résultat :</span> Tu ne peux plus perdre → si ça retombe, tu sors à zéro.
                        </div>
                      </div>
                    </div>

                    {/* B) Trailing stop */}
                    <div className="p-4 bg-green-500/10 rounded-lg border border-green-500/20">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="w-6 h-6 rounded-full bg-green-500 text-white text-sm flex items-center justify-center font-bold">B</span>
                        <span className="font-semibold text-green-400">Sécuriser un gain (Trailing stop manuel)</span>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        <p>On sécurise <strong>toujours</strong> une partie des gains à <strong>2.1 R:R</strong> (voir même moins).</p>
                        <p className="mt-1">Et on peut même remonter le SL à break-even → <strong className="text-green-400">100% gagnant assuré</strong>.</p>
                      </div>
                    </div>

                    {/* C) Laisser courir */}
                    <div className="p-4 bg-purple-500/10 rounded-lg border border-purple-500/20">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="w-6 h-6 rounded-full bg-purple-500 text-white text-sm flex items-center justify-center font-bold">C</span>
                        <span className="font-semibold text-purple-400">Laisser courir après sécurisation</span>
                      </div>
                      <div className="text-sm text-muted-foreground space-y-1">
                        <p><strong>1)</strong> On a sécurisé du gain (TP partiel)</p>
                        <p><strong>2)</strong> On a sécurisé la perte avec le SL au BE</p>
                        <p><strong>3)</strong> On garde <strong>10 à 20%</strong> pour jouer une poursuite du mouvement si on le sent</p>
                      </div>
                    </div>
                  </div>

                  {/* Warning SL */}
                  <div className="p-4 bg-red-500/10 rounded-lg border border-red-500/30">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-5 h-5 text-red-400" />
                      <span className="font-bold text-red-400">ATTENTION - Erreur de débutant</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      <strong>Ne déplace JAMAIS ton SL plus loin</strong> qu'au départ pour "laisser respirer" → tu augmentes ton risque !
                    </p>
                    <p className="text-sm text-muted-foreground mt-2">
                      <strong>Règle d'or :</strong> Déplacement de SL = <strong className="text-green-400">uniquement dans le sens de la sécurité</strong> (réduire le risque ou sécuriser un gain).
                    </p>
                  </div>
                </div>

                {/* Section Take-Profit */}
                <div className="space-y-4">
                  <h4 className="font-bold text-lg flex items-center gap-2 text-green-400">
                    <TrendingUp className="w-5 h-5" />
                    2. Gestion du Take-Profit (TP)
                  </h4>

                  {/* Différence SL vs TP */}
                  <div className="p-4 bg-muted/30 rounded-lg border border-border/30">
                    <h5 className="font-semibold mb-3">Différence entre SL et TP</h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="p-3 bg-red-500/10 rounded border border-red-500/20">
                        <div className="font-semibold text-red-400 text-sm">Stop-Loss (SL)</div>
                        <p className="text-xs text-muted-foreground mt-1">Protège ton capital → limite ta perte max</p>
                        <Badge className="mt-2 bg-red-500/20 text-red-400 border-red-500/30">OBLIGATOIRE</Badge>
                      </div>
                      <div className="p-3 bg-green-500/10 rounded border border-green-500/20">
                        <div className="font-semibold text-green-400 text-sm">Take-Profit (TP)</div>
                        <p className="text-xs text-muted-foreground mt-1">Verrouille ton gain → clôture automatiquement</p>
                        <Badge className="mt-2 bg-yellow-500/20 text-yellow-400 border-yellow-500/30">OPTIONNEL</Badge>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-3">
                      <Info className="w-3 h-3 inline mr-1" />
                      Beaucoup de traders préfèrent gérer le TP "manuellement" pour laisser courir la tendance.
                    </p>
                  </div>

                  {/* Stratégies TP */}
                  <div className="space-y-3">
                    <h5 className="font-semibold text-sm text-muted-foreground">Stratégies possibles avec le TP :</h5>

                    {/* A) TP fixe */}
                    <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/20">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="w-6 h-6 rounded-full bg-blue-500 text-white text-sm flex items-center justify-center font-bold">A</span>
                        <span className="font-semibold text-blue-400">TP fixe (classique)</span>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        <p>Tu fixes un objectif clair dès le début (ex : 119k). Si le prix l'atteint → tu encaisses.</p>
                        <div className="grid grid-cols-2 gap-2 mt-2">
                          <div className="p-2 bg-green-500/10 rounded text-xs">
                            <CheckCircle className="w-3 h-3 text-green-400 inline mr-1" />
                            Simple, parfait en swing
                          </div>
                          <div className="p-2 bg-red-500/10 rounded text-xs">
                            <AlertTriangle className="w-3 h-3 text-red-400 inline mr-1" />
                            Parfois tu sors trop tôt
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* B) TP multiple */}
                    <div className="p-4 bg-green-500/10 rounded-lg border border-green-500/20">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="w-6 h-6 rounded-full bg-green-500 text-white text-sm flex items-center justify-center font-bold">B</span>
                        <span className="font-semibold text-green-400">TP multiple (prise de profit partielle)</span>
                        <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs">RECOMMANDÉ</Badge>
                      </div>
                      <div className="text-sm text-muted-foreground space-y-3">
                        <div className="p-3 bg-background/50 rounded">
                          <p className="font-semibold text-primary mb-2">Si R:R à 3.1 :</p>
                          <ul className="space-y-1 text-xs">
                            <li>• Sécuriser <strong>50%</strong> à R:R 2.1</li>
                            <li>• Sécuriser <strong>85%</strong> à R:R 3.1</li>
                            <li>• Laisser courir <strong>15%</strong></li>
                          </ul>
                        </div>
                        <div className="p-3 bg-background/50 rounded">
                          <p className="font-semibold text-primary mb-2">Si R:R à 2.1 :</p>
                          <ul className="space-y-1 text-xs">
                            <li>• Sécuriser <strong>50%</strong> à R:R 1.5</li>
                            <li>• Sécuriser <strong>85%</strong> à R:R 2.1</li>
                            <li>• Laisser courir <strong>15%</strong></li>
                          </ul>
                        </div>
                        <div className="p-2 bg-green-500/20 rounded text-xs">
                          <CheckCircle className="w-3 h-3 text-green-400 inline mr-1" />
                          <strong>Bon équilibre</strong> entre sécurité et potentiel de gain
                        </div>
                      </div>
                    </div>

                    {/* C) SL mobile uniquement */}
                    <div className="p-4 bg-purple-500/10 rounded-lg border border-purple-500/20">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="w-6 h-6 rounded-full bg-purple-500 text-white text-sm flex items-center justify-center font-bold">C</span>
                        <span className="font-semibold text-purple-400">Pas de TP, uniquement un SL mobile</span>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        <p className="mb-2">Tu entres (ex: 113k), mais tu ne mets <strong>aucun TP</strong>. Tu déplaces ton SL au fur et à mesure :</p>
                        <div className="p-3 bg-background/50 rounded space-y-1 text-xs font-mono">
                          <p>Prix 115k → SL remonte à <span className="text-yellow-400">113k</span></p>
                          <p>Prix 117k → SL remonte à <span className="text-yellow-400">115k</span></p>
                          <p>Prix 120k → SL remonte à <span className="text-yellow-400">118k</span></p>
                        </div>
                        <p className="mt-2 text-xs">Tu sors <strong>uniquement quand le marché te sort</strong>.</p>
                        <div className="grid grid-cols-2 gap-2 mt-2">
                          <div className="p-2 bg-green-500/10 rounded text-xs">
                            <CheckCircle className="w-3 h-3 text-green-400 inline mr-1" />
                            Parfait pour trend following
                          </div>
                          <div className="p-2 bg-yellow-500/10 rounded text-xs">
                            <AlertTriangle className="w-3 h-3 text-yellow-400 inline mr-1" />
                            Pas de gain garanti
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Conclusion */}
                  <div className="p-4 bg-gradient-to-r from-green-500/20 to-primary/20 rounded-lg border border-green-500/30">
                    <h5 className="font-bold text-lg mb-3 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-400" />
                      Conclusion
                    </h5>
                    <div className="space-y-3 text-sm">
                      <div className="p-3 bg-background/50 rounded">
                        <p className="font-semibold text-red-400 mb-1">Stop-Loss (SL) :</p>
                        <ul className="text-muted-foreground text-xs space-y-1">
                          <li>• <strong className="text-green-400">OUI</strong> tu peux le déplacer à tout moment</li>
                          <li>• <strong className="text-red-400">JAMAIS</strong> pour augmenter ta perte potentielle</li>
                          <li>• <strong className="text-green-400">TOUJOURS</strong> pour réduire ton risque ou protéger tes gains</li>
                        </ul>
                      </div>
                      <div className="p-3 bg-background/50 rounded">
                        <p className="font-semibold text-green-400 mb-1">Take-Profit (TP) :</p>
                        <ul className="text-muted-foreground text-xs space-y-1">
                          <li>• Tu peux le <strong>déplacer plus haut</strong> si le marché est fort</li>
                          <li>• Tu peux l'<strong>enlever</strong> et te reposer uniquement sur un SL mobile</li>
                          <li>• Logique différente : <strong>toujours dans le sens du gain</strong></li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </SectionCard>
        </TabsContent>
        </div>

        {/* CTA overlay for unauthenticated users */}
        {!isAuthenticated && (
          <div
            className="absolute inset-x-0 bottom-0 z-20 pointer-events-none"
            style={{ top: '260px' }}
            data-testid="learn-gate-overlay"
          >
            <div className="h-full w-full bg-gradient-to-b from-transparent via-background/85 to-background flex items-end justify-center pb-6 pointer-events-auto">
              <Card className="max-w-lg mx-4 bg-card/95 backdrop-blur-md border-primary/30 shadow-2xl">
                <CardContent className="p-6 text-center space-y-4">
                  <div className="mx-auto w-12 h-12 rounded-full bg-primary/15 flex items-center justify-center">
                    <Lock className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold flex items-center justify-center gap-2">
                    <Sparkles className="w-5 h-5 text-yellow-400" />
                    Formation complète réservée aux membres
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Créez un compte gratuit en 30 secondes pour débloquer toute la formation :
                    règles, lexique, indicateurs techniques, concepts ICT, news éco et fiches pratiques.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-2 justify-center pt-2">
                    <Link to="/register" className="flex-1">
                      <Button
                        className="w-full bg-primary hover:bg-primary/90 font-bold"
                        data-testid="learn-gate-register-btn"
                      >
                        Créer un compte gratuit
                      </Button>
                    </Link>
                    <Link to="/login" className="flex-1">
                      <Button
                        variant="outline"
                        className="w-full border-white/20"
                        data-testid="learn-gate-login-btn"
                      >
                        Se connecter
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
        </div>
      </Tabs>
    </div>
  );
};

export default Learn;
